import { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from "react";
import type { CSSProperties, Dispatch, SetStateAction, KeyboardEvent as ReactKeyboardEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  DragOverlay,
  useDraggable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus, Trash2, Pencil, Settings, GripVertical, Link2, Unlink2,
  UtensilsCrossed, Layers, ImageIcon, RefreshCw,
  Columns3, Copy, Check, X,
  TableProperties, Percent, DollarSign, AlertCircle, Wand2,
  Ban, CalendarDays,
  Flame, Wheat, Coffee, Sandwich, Pizza, Milk, Beef, ChefHat,
  Droplets, PackagePlus, PenLine, ArrowLeft, Zap, Maximize2, Soup, Salad,
  Thermometer, CircleDot, CheckSquare, SlidersHorizontal,
} from "lucide-react";
import MenuImportWizard from "./MenuImportWizard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem,
} from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Extracts the base fields required by the RMP item-update endpoint.
 * name, description, price, service, and sort must be present on every POST.
 * `type` is intentionally omitted here — it is optional per Eatzy API Task #78
 * and is only included in the full save payload when explicitly selected by the user.
 * Inline saves (name/price edits) must not send `type` to avoid enum validation errors.
 */
function itemRequiredFields(item: any) {
  const rawPrices = item.prices ?? {};
  const price =
    rawPrices.takeout?.base ??
    rawPrices.base ??
    (typeof rawPrices.takeout === "number" ? rawPrices.takeout : null) ??
    item.price ??
    0;
  const service = Array.isArray(item.service)
    ? item.service
    : Array.isArray(item.services)
    ? item.services
    : ["takeout", "delivery"];
  return {
    name: item.name ?? "",
    description: item.description ?? "",
    price: Number(price),
    service,
    sort: item.sort ?? item.sort_order ?? 0,
  };
}

function lamHeaders(token: string): Record<string, string> {
  const jwt = localStorage.getItem("auth_token") || localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    "x-eatzy-token": token,
    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
  };
}

async function lamFetch(url: string, token: string, opts: RequestInit = {}) {
  const res = await fetch(url, {
    ...opts,
    headers: { ...lamHeaders(token), ...((opts.headers as Record<string, string>) || {}) },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text.slice(0, 200)}`);
  }
  return res.json();
}

function formatLabel(fmt: string): string {
  if (fmt === "radio") return "Single";
  if (fmt === "check" || fmt === "checkbox") return "Multiple";
  if (fmt === "stepper") return "Qty";
  if (fmt === "drop") return "Dropdown";
  return fmt ?? "";
}

function deriveFormat(min: string, max: string): string {
  const minN = Number(min);
  const maxN = Number(max);
  if (minN === 1 && maxN === 1) return "radio";
  return "check";
}

function toApiFormat(fmt: string): string {
  if (fmt === "checkbox" || fmt === "stepper") return "check";
  return fmt;
}

function configSummary(format: string, required: boolean, max: string, maxPerAnswer: string): string {
  const maxN = Number(max) || 1;
  const mpa = Number(maxPerAnswer) || 1;
  if (format === "radio") {
    return required
      ? "Customer must pick exactly 1 option."
      : "Customer can pick 1 option, or skip entirely.";
  }
  if (format === "stepper") {
    const base = required
      ? `Customer must pick between 1 and ${maxN} option${maxN === 1 ? "" : "s"}`
      : `Customer can pick up to ${maxN} option${maxN === 1 ? "" : "s"} (or skip)`;
    return `${base}, with up to ${mpa} of each option.`;
  }
  if (required) {
    return `Customer must pick between 1 and ${maxN} option${maxN === 1 ? "" : "s"}.`;
  }
  return `Customer can pick up to ${maxN} option${maxN === 1 ? "" : "s"} (or skip entirely).`;
}

function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [breakpoint]);
  return isMobile;
}

// ─── SORTABLE CATEGORY ROW ────────────────────────────────────────────────────

function SortableCategoryRow({
  cat, isSelected, itemCount, onSelect, onEdit, onDelete, onEightySix,
}: {
  cat: any; isSelected: boolean; itemCount: number;
  onSelect: () => void; onEdit: () => void; onDelete: () => void; onEightySix: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `cat-${cat.id}`,
    data: { type: "category", catId: cat.id },
  });
  const style: CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <div
      ref={setNodeRef} style={style} onClick={onSelect}
      className={`flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer group hover-elevate transition-colors border ${
        isSelected ? "bg-primary/10 border-primary/30" : "border-transparent"
      }`}
    >
      <button {...listeners} {...attributes} onClick={(e) => e.stopPropagation()}
        className="text-muted-foreground cursor-grab active:cursor-grabbing shrink-0 touch-none">
        <GripVertical className="w-4 h-4" />
      </button>
      {cat.image_url && (
        <img src={cat.image_url} alt="" className="w-7 h-7 rounded object-cover shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-tight">{cat.name}</p>
        <p className="text-xs text-muted-foreground leading-tight mt-0.5">{itemCount} item{itemCount !== 1 ? "s" : ""}</p>
      </div>
      <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 shrink-0" onClick={(e) => e.stopPropagation()}>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onEdit}><Pencil className="w-3 h-3" /></Button>
        {itemCount > 0
          ? <Button size="icon" variant="ghost" className="h-6 w-6" title="86 all items in this category" onClick={onEightySix}><Ban className="w-3 h-3 text-orange-500" /></Button>
          : <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onDelete}><Trash2 className="w-3 h-3 text-destructive" /></Button>
        }
      </div>
    </div>
  );
}

// ─── SORTABLE ITEM ROW ────────────────────────────────────────────────────────

function SortableItemRow({
  item, categories, isExpanded, isDropTarget, linkedCount, uploadingFor,
  editingField, editingValue, hideCategory,
  onToggleExpand, onOpenDetails, onDelete, onRestore, onDuplicate, onImageUpload,
  onStartInline, onInlineChange, onInlineSave, onInlineKeyDown, onInlineCancel,
  onEightySix, onMakeAvailable, accordionProps,
}: {
  item: any; categories: any[]; isExpanded: boolean; isDropTarget: boolean;
  linkedCount: number; uploadingFor: number | null;
  editingField: string | null; editingValue: string;
  hideCategory?: boolean;
  onToggleExpand: () => void; onOpenDetails?: () => void;
  onDelete: () => void; onRestore: () => void;
  onDuplicate: () => void; onImageUpload: (file: File) => void;
  onStartInline: (field: "name" | "price", value: string) => void;
  onInlineChange: (v: string) => void;
  onInlineSave: (field: "name" | "price") => void;
  onInlineKeyDown: (e: ReactKeyboardEvent, field: "name" | "price") => void;
  onInlineCancel: () => void;
  onEightySix: () => void;
  onMakeAvailable: () => void;
  accordionProps?: AccordionProps;
}) {
  const cancelingInlineRef = useRef(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `item-${item.id}`,
    data: { type: "item", itemId: item.id },
  });
  const style: CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  const isDeleted = item.status === "deleted" || item.status === "inactive";
  const is86d = item.available === false;
  const thumb = item.media?.logo || item.media?.thumbnail || item.media?.item_thumbnail;
  const catName = categories.find((c: any) => c.id === item.category)?.name;
  const price = item.prices?.base ??
    (typeof item.prices?.takeout === "number" ? item.prices.takeout : null) ??
    item.price ?? null;

  const priceRef = useRef<HTMLInputElement>(null);

  const editingPrice = editingField === `price-${item.id}`;

  return (
    <div
      ref={setNodeRef} style={style}
      className={`flex flex-col rounded-md border bg-card transition-colors ${
        isDeleted ? "opacity-50" : is86d ? "opacity-70" : ""
      } ${isDropTarget ? "border-primary border-2 bg-primary/5" : isExpanded ? "border-primary/40 bg-primary/5" : "border-border"}`}
    >
      {/* ── Summary row ── */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        {/* Drag handle */}
        <button {...listeners} {...attributes}
          className="text-muted-foreground cursor-grab active:cursor-grabbing shrink-0 touch-none" aria-label="Reorder">
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Thumbnail — clickable to expand/collapse */}
        <button className="shrink-0" onClick={onToggleExpand} title={isExpanded ? "Collapse" : "Expand item details"}>
          {thumb ? (
            <img src={thumb} alt={item.name} className="w-10 h-10 rounded-md object-cover" loading="lazy" />
          ) : (
            <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center hover-elevate">
              <UtensilsCrossed className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
        </button>

        {/* Name + category — tap anywhere here to expand/collapse */}
        <div
          className="flex-1 min-w-0 cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={onToggleExpand}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggleExpand(); } }}
          title={isExpanded ? "Collapse" : "Tap to edit details"}
        >
          <p className={`text-sm font-medium leading-tight truncate ${is86d ? "line-through text-muted-foreground" : ""}`}>
            {item.name || <span className="text-muted-foreground italic">Unnamed</span>}
          </p>
          {catName && !hideCategory && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{catName}</p>
          )}
        </div>

        {/* Price — inline editable */}
        <div className="shrink-0 w-20">
          {editingPrice ? (
            <div className="flex items-center gap-1">
              <Input
                ref={priceRef}
                value={editingValue}
                autoFocus
                type="number"
                step="0.01"
                onChange={(e) => onInlineChange(e.target.value)}
                onBlur={() => { if (!cancelingInlineRef.current) onInlineSave("price"); }}
                onKeyDown={(e) => onInlineKeyDown(e, "price")}
                className="h-7 text-xs py-0 text-right w-20"
              />
              <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={() => onInlineSave("price")}><Check className="w-3 h-3 text-primary" /></Button>
              <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0"
                onMouseDown={() => { cancelingInlineRef.current = true; }}
                onClick={() => { cancelingInlineRef.current = false; onInlineCancel(); }}
              ><X className="w-3 h-3" /></Button>
            </div>
          ) : (
            <button
              onClick={() => onStartInline("price", price != null ? String(price) : "")}
              className="text-sm font-medium hover:text-primary w-full text-right"
              title="Click to edit price"
            >
              {price != null ? `$${Number(price).toFixed(2)}` : <span className="text-muted-foreground text-xs">+ price</span>}
            </button>
          )}
        </div>

        {/* Status badges */}
        {isDeleted && (
          <Badge variant="destructive" className="text-xs shrink-0">Deleted</Badge>
        )}
        {is86d && !isDeleted && (
          <Badge variant="secondary" className="text-xs shrink-0 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">86'd</Badge>
        )}

        {/* Quick 86 / restore CTA — always visible, compact label */}
        {!isDeleted && (
          is86d ? (
            <Button size="sm" variant="ghost" className="h-7 shrink-0 text-primary gap-1 px-2 text-xs" onClick={(e) => { e.stopPropagation(); onMakeAvailable(); }}>
              <RefreshCw className="w-3 h-3" /> Restore
            </Button>
          ) : (
            <Button size="sm" variant="ghost" className="h-7 shrink-0 text-orange-500 gap-1 px-2 text-xs" onClick={(e) => { e.stopPropagation(); onEightySix(); }}>
              <Ban className="w-3 h-3" /> 86 It
            </Button>
          )
        )}

        {/* Duplicate icon button */}
        <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={(e) => { e.stopPropagation(); onDuplicate(); }} title="Duplicate item">
          <Copy className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* ── Accordion content ── */}
      {isExpanded && accordionProps && (
        <ItemAccordionContent {...accordionProps} />
      )}
    </div>
  );
}

// ─── EIGHTY-SIX MODAL ────────────────────────────────────────────────────────

function EightySixModal({
  open, item, onClose, onConfirm, isPending = false,
}: {
  open: boolean; item: any | null; onClose: () => void;
  onConfirm: (body: Record<string, any>, useUpdate?: boolean) => void;
  isPending?: boolean;
}) {
  const [mode, setMode] = useState<"indefinite" | "today" | "date" | "forever">("indefinite");
  const [untilDate, setUntilDate] = useState("");

  useEffect(() => {
    if (open) { setMode("indefinite"); setUntilDate(""); }
  }, [open]);

  function confirm() {
    if (mode === "today") {
      const start = new Date(); start.setHours(0, 0, 0, 0);
      const end = new Date(); end.setHours(23, 59, 59, 999);
      onConfirm({ unavailable_from: start.toISOString(), unavailable_to: end.toISOString() }, true);
    } else if (mode === "date" && untilDate) {
      const now = new Date();
      const end = new Date(untilDate); end.setHours(23, 59, 59, 999);
      onConfirm({ unavailable_from: now.toISOString(), unavailable_to: end.toISOString() }, true);
    } else {
      onConfirm({}, false);
    }
  }

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ban className="w-4 h-4 text-orange-500" /> 86 It</DialogTitle>
          <DialogDescription>How long should <strong>"{item.name}"</strong> be unavailable?</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {[
            { key: "indefinite", label: "Until I add it back", desc: "Stays 86'd until you restore it" },
            { key: "today", label: "Just today", desc: "Available again tomorrow morning" },
            { key: "date", label: "Until a specific date", desc: "Pick the date it comes back" },
            { key: "forever", label: "Remove from menu", desc: "Hides item indefinitely until you restore it" },
          ].map(({ key, label, desc }) => (
            <button
              key={key}
              onClick={() => setMode(key as typeof mode)}
              className={`w-full text-left px-3 py-2.5 rounded-md border text-sm transition-colors ${
                mode === key
                  ? key === "forever"
                    ? "border-destructive bg-destructive/5 text-destructive"
                    : "border-primary bg-primary/5"
                  : "border-border hover-elevate"
              }`}
            >
              <div className="font-medium">{label}</div>
              <div className="text-xs text-muted-foreground">{desc}</div>
            </button>
          ))}
          {mode === "date" && (
            <div className="pt-1 space-y-1">
              <Label className="text-xs">Available again after:</Label>
              <Input type="date" value={untilDate} onChange={(e) => setUntilDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button
            variant={mode === "forever" ? "destructive" : "default"}
            onClick={confirm}
            disabled={(mode === "date" && !untilDate) || isPending}
          >
            {isPending ? "Saving…" : mode === "forever" ? "Remove from Menu" : "86 It"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── MODIFIER TEMPLATES + EDIT MODIFIER GROUP DIALOG ─────────────────────────

type ModifierTemplate = {
  id: string;
  name: string;
  category: string;
  icon: React.FC<{ className?: string }>;
  format: "radio" | "checkbox" | "stepper";
  required: boolean;
  min: number;
  max: number;
  maxPerAnswer?: number;
  answers: { text: string; price: number }[];
};

const MODIFIER_TEMPLATES: ModifierTemplate[] = [
  { id: "doneness", name: "Doneness", category: "Cooking", icon: Flame, format: "radio", required: true, min: 1, max: 1,
    answers: [{ text: "Rare", price: 0 }, { text: "Med Rare", price: 0 }, { text: "Medium", price: 0 }, { text: "Med Well", price: 0 }, { text: "Well Done", price: 0 }] },
  { id: "spice_level", name: "Spice Level", category: "Cooking", icon: Zap, format: "radio", required: false, min: 0, max: 1,
    answers: [{ text: "Mild", price: 0 }, { text: "Medium", price: 0 }, { text: "Hot", price: 0 }, { text: "Extra Hot", price: 0 }] },
  { id: "size", name: "Size", category: "Portions", icon: Maximize2, format: "radio", required: true, min: 1, max: 1,
    answers: [{ text: "Small", price: 0 }, { text: "Medium", price: 0 }, { text: "Large", price: 0 }] },
  { id: "protein", name: "Protein Choice", category: "Cooking", icon: Beef, format: "radio", required: true, min: 1, max: 1,
    answers: [{ text: "Chicken", price: 0 }, { text: "Beef", price: 0 }, { text: "Fish", price: 0 }, { text: "Shrimp", price: 0 }, { text: "Tofu", price: 0 }, { text: "Veggie", price: 0 }] },
  { id: "bread", name: "Bread Choice", category: "Condiments", icon: Wheat, format: "radio", required: true, min: 1, max: 1,
    answers: [{ text: "White", price: 0 }, { text: "Wheat", price: 0 }, { text: "Sourdough", price: 0 }, { text: "Rye", price: 0 }, { text: "GF Wrap", price: 0 }] },
  { id: "dressing", name: "Dressing", category: "Condiments", icon: Droplets, format: "radio", required: true, min: 1, max: 1,
    answers: [{ text: "Ranch", price: 0 }, { text: "Blue Cheese", price: 0 }, { text: "Caesar", price: 0 }, { text: "Italian", price: 0 }, { text: "Honey Mustard", price: 0 }, { text: "No Dressing", price: 0 }] },
  { id: "sauce", name: "Sauce", category: "Condiments", icon: Droplets, format: "radio", required: true, min: 1, max: 1,
    answers: [{ text: "Ketchup", price: 0 }, { text: "Mayo", price: 0 }, { text: "Mustard", price: 0 }, { text: "Sriracha", price: 0 }, { text: "BBQ", price: 0 }, { text: "Aioli", price: 0 }, { text: "None", price: 0 }] },
  { id: "sandwich_toppings", name: "Sandwich Toppings", category: "Toppings", icon: Sandwich, format: "checkbox", required: false, min: 0, max: 10,
    answers: [{ text: "Lettuce", price: 0 }, { text: "Tomato", price: 0 }, { text: "Onion", price: 0 }, { text: "Pickles", price: 0 }, { text: "Jalapeños", price: 0 }, { text: "Avocado", price: 0 }, { text: "Bacon", price: 0 }] },
  { id: "pizza_toppings", name: "Pizza Toppings", category: "Toppings", icon: Pizza, format: "checkbox", required: false, min: 0, max: 8,
    answers: [{ text: "Pepperoni", price: 0 }, { text: "Mushrooms", price: 0 }, { text: "Peppers", price: 0 }, { text: "Onions", price: 0 }, { text: "Olives", price: 0 }, { text: "Sausage", price: 0 }, { text: "Bacon", price: 0 }] },
  { id: "salad_toppings", name: "Salad Toppings", category: "Toppings", icon: Salad, format: "checkbox", required: false, min: 0, max: 8,
    answers: [{ text: "Croutons", price: 0 }, { text: "Bacon", price: 0 }, { text: "Cheese", price: 0 }, { text: "Tomato", price: 0 }, { text: "Cucumber", price: 0 }] },
  { id: "side_dish", name: "Side Dish", category: "Sides", icon: Soup, format: "radio", required: true, min: 1, max: 1,
    answers: [{ text: "Fries", price: 0 }, { text: "Salad", price: 0 }, { text: "Coleslaw", price: 0 }, { text: "Soup", price: 0 }, { text: "Onion Rings", price: 0 }] },
  { id: "extras", name: "Extras / Add-ons", category: "Add-ons", icon: PackagePlus, format: "checkbox", required: false, min: 0, max: 5,
    answers: [{ text: "Extra Cheese", price: 0 }, { text: "Extra Sauce", price: 0 }, { text: "Guacamole", price: 0 }, { text: "Avocado", price: 0 }, { text: "Bacon", price: 0 }] },
  { id: "temperature", name: "Temperature", category: "Beverages", icon: Thermometer, format: "radio", required: true, min: 1, max: 1,
    answers: [{ text: "Hot", price: 0 }, { text: "Iced", price: 0 }, { text: "Blended", price: 0 }] },
  { id: "milk", name: "Milk Choice", category: "Beverages", icon: Milk, format: "radio", required: true, min: 1, max: 1,
    answers: [{ text: "Whole", price: 0 }, { text: "Skim", price: 0 }, { text: "Oat", price: 0 }, { text: "Almond", price: 0 }, { text: "Soy", price: 0 }] },
  { id: "shots", name: "Espresso Shots", category: "Beverages", icon: Coffee, format: "radio", required: true, min: 1, max: 1,
    answers: [{ text: "Single", price: 0 }, { text: "Double", price: 0 }, { text: "Triple", price: 0 }] },
];

const TEMPLATE_CATEGORIES = [
  "Cooking", "Portions", "Condiments", "Toppings", "Sides", "Add-ons", "Beverages",
];

type PlannedAnswer = { id: string; text: string; price: string };

// ─── EDIT MODIFIER GROUP DIALOG ───────────────────────────────────────────────

function EditModifierGroupDialog({
  open, onClose, question, restaurantId, token, onRefresh, onCreated,
}: {
  open: boolean; onClose: () => void;
  question: any | null; restaurantId: string; token: string;
  onRefresh: () => void;
  onCreated?: (newId: number) => void;
}) {
  const { toast } = useToast();
  const isCreateMode = question === null;

  const [grpForm, setGrpForm] = useState({
    name: "", text: "", format: "radio", required: true, min: "1", max: "1", maxPerAnswer: "1",
  });
  const [editAnswerField, setEditAnswerField] = useState<string | null>(null);
  const [editAnswerValue, setEditAnswerValue] = useState("");
  const [newText, setNewText] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [eightySixAnswerTarget, setEightySixAnswerTarget] = useState<any>(null);

  // Create-mode two-step flow
  const [phase, setPhase] = useState<"pick" | "form">("pick");
  const [plannedAnswers, setPlannedAnswers] = useState<PlannedAnswer[]>([]);
  const [addRowText, setAddRowText] = useState("");
  const [addRowPrice, setAddRowPrice] = useState("");
  const [creating, setCreating] = useState(false);
  const [createProgress, setCreateProgress] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => {
    if (question) {
      const isCheckFormat = question.format === "check" || question.format === "checkbox";
      const maxPerAns = Number(question.max_per_answer ?? 1);
      const derivedFormat = isCheckFormat && maxPerAns > 1 ? "stepper" : (question.format ?? "radio");
      setGrpForm({
        name: question.name ?? "",
        text: question.text ?? "",
        format: derivedFormat,
        required: question.required ?? true,
        min: question.min != null ? String(question.min) : "1",
        max: question.max != null ? String(question.max) : "1",
        maxPerAnswer: maxPerAns > 1 ? String(maxPerAns) : "1",
      });
    } else {
      setGrpForm({ name: "", text: "", format: "radio", required: true, min: "1", max: "1", maxPerAnswer: "1" });
      setPhase("pick");
      setPlannedAnswers([]);
      setAddRowText("");
      setAddRowPrice("");
    }
    setEditAnswerField(null);
    setEditAnswerValue("");
    setNewText("");
    setNewPrice("");
    setEightySixAnswerTarget(null);
  }, [open, question?.id]);

  const rawEditAnswers: any[] = question
    ? (Array.isArray(question.answers) ? question.answers
       : Array.isArray(question.answer) ? question.answer : [])
    : [];
  const answers: any[] = [...rawEditAnswers].sort((a: any, b: any) => (a.sort ?? 0) - (b.sort ?? 0));

  // ── Async create: group + sequential answer batch ──
  async function doCreate() {
    if (!grpForm.name.trim()) return;
    setCreating(true);
    setCreateProgress(null);
    try {
      const data: any = await lamFetch(`/api/admin/rmp/${restaurantId}/questions`, token, {
        method: "PUT",
        body: JSON.stringify({
          name: grpForm.name.trim(),
          text: grpForm.text.trim() || grpForm.name.trim(),
          format: toApiFormat(grpForm.format),
          required: grpForm.required,
          min: grpForm.required ? 1 : 0,
          max: grpForm.format === "radio" ? 1 : Math.max(2, Number(grpForm.max) || 2),
          max_per_answer: grpForm.format === "stepper"
            ? Math.max(2, Number(grpForm.maxPerAnswer) || 3)
            : 1,
        }),
      });
      const newId = data?.id ?? data?.question_id;
      const validAnswers = plannedAnswers.filter((a) => a.text.trim());
      if (newId && validAnswers.length > 0) {
        for (let i = 0; i < validAnswers.length; i++) {
          setCreateProgress({ done: i, total: validAnswers.length });
          const ans = validAnswers[i];
          await lamFetch(`/api/admin/rmp/${restaurantId}/questions/${newId}/answer`, token, {
            method: "PUT",
            body: JSON.stringify({
              text: ans.text.trim(),
              price: parseFloat(ans.price) || 0,
              weight: 1,
              sort: i,
            }),
          });
        }
        setCreateProgress({ done: validAnswers.length, total: validAnswers.length });
      }
      const optionCount = validAnswers.length;
      toast({
        title: "Modifier group created",
        description: optionCount > 0
          ? `Group created with ${optionCount} option${optionCount === 1 ? "" : "s"}.`
          : "Add options using the edit panel that just opened.",
      });
      onRefresh();
      onClose();
      if (newId && onCreated) onCreated(Number(newId));
    } catch (e: any) {
      toast({ title: "Failed to create group", description: e.message, variant: "destructive" });
    } finally {
      setCreating(false);
      setCreateProgress(null);
    }
  }

  function selectTemplate(tpl: ModifierTemplate) {
    setGrpForm({
      name: tpl.name,
      text: tpl.name,
      format: tpl.format,
      required: tpl.required,
      min: String(tpl.min),
      max: String(tpl.max),
      maxPerAnswer: tpl.maxPerAnswer ? String(tpl.maxPerAnswer) : "1",
    });
    setPlannedAnswers(
      tpl.answers.map((a, i) => ({
        id: `tpl-${i}-${Date.now()}`,
        text: a.text,
        price: a.price > 0 ? String(a.price) : "",
      }))
    );
    setPhase("form");
  }

  function startScratch() {
    setGrpForm({ name: "", text: "", format: "radio", required: true, min: "1", max: "1", maxPerAnswer: "1" });
    setPlannedAnswers([]);
    setPhase("form");
  }

  function addPlannedRow() {
    if (!addRowText.trim()) return;
    setPlannedAnswers((prev) => [
      ...prev,
      { id: `row-${Date.now()}`, text: addRowText.trim(), price: addRowPrice },
    ]);
    setAddRowText("");
    setAddRowPrice("");
  }

  function removePlannedRow(id: string) {
    setPlannedAnswers((prev) => prev.filter((a) => a.id !== id));
  }

  function updatePlannedRow(id: string, field: "text" | "price", value: string) {
    setPlannedAnswers((prev) =>
      prev.map((a) => (a.id === id ? { ...a, [field]: value } : a))
    );
  }

  // API only accepts { name, text } on group update — format/min/max are silently ignored.
  const updateGrpM = useMutation({
    mutationFn: () =>
      lamFetch(`/api/admin/rmp/${restaurantId}/questions/${question?.id}`, token, {
        method: "POST",
        body: JSON.stringify({
          name: grpForm.name.trim(),
          text: grpForm.text,
        }),
      }),
    onSuccess: () => { toast({ title: "Group updated" }); onRefresh(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  // API requires { text, price, weight, sort } — all 4 fields every time.
  const addAnswerM = useMutation({
    mutationFn: (data: { text: string; price: number; weight: number; sort: number }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/questions/${question?.id}/answer`, token, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      toast({ title: "Option added" });
      setNewText(""); setNewPrice("");
      onRefresh();
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  // API requires full body { text, weight, price, sort } — no partial updates.
  const updateAnswerM = useMutation({
    mutationFn: ({ answerId, data }: { answerId: number; data: { text: string; weight: number; price: number; sort: number } }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/questions/${question?.id}/answer/${answerId}`, token, {
        method: "POST", body: JSON.stringify(data),
      }),
    onSuccess: () => {
      toast({ title: "Option updated" });
      setEditAnswerField(null);
      onRefresh();
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const deleteAnswerM = useMutation({
    mutationFn: ({ answerId }: { answerId: number; body?: Record<string, any> }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/questions/${question?.id}/answer/${answerId}`, token, {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast({ title: "Option 86'd" });
      setEightySixAnswerTarget(null);
      onRefresh();
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const restoreAnswerM = useMutation({
    mutationFn: (answerId: number) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/questions/${question?.id}/answer/${answerId}/restore`, token, {
        method: "PUT",
      }),
    onSuccess: () => {
      toast({ title: "Option restored" });
      onRefresh();
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  // Always send the complete answer body { text, weight, price, sort } — API has no partial-update support.
  function buildFullAnswerBody(ans: any, overrides: Partial<{ text: string; price: number }>): { text: string; weight: number; price: number; sort: number } {
    return {
      text: overrides.text ?? (ans.text ?? ""),
      weight: ans.weight ?? 1,
      price: overrides.price ?? (ans.prices?.base ?? ans.prices?.default ?? ans.price ?? 0),
      sort: ans.sort ?? 0,
    };
  }

  function saveAnswerInline(ans: any) {
    if (!editAnswerField || !question) { setEditAnswerField(null); return; }
    if (editAnswerField.startsWith("text-")) {
      if (!editAnswerValue.trim()) { setEditAnswerField(null); return; }
      updateAnswerM.mutate({ answerId: ans.id, data: buildFullAnswerBody(ans, { text: editAnswerValue.trim() }) });
    } else if (editAnswerField.startsWith("price-")) {
      if (editAnswerValue.trim() === "") {
        updateAnswerM.mutate({ answerId: ans.id, data: buildFullAnswerBody(ans, { price: 0 }) });
      } else {
        const num = parseFloat(editAnswerValue);
        if (!isNaN(num)) updateAnswerM.mutate({ answerId: ans.id, data: buildFullAnswerBody(ans, { price: num }) });
        else setEditAnswerField(null);
      }
    }
  }

  function doAddAnswer() {
    if (!newText.trim() || !question) return;
    const priceNum = newPrice !== "" ? parseFloat(newPrice) : 0;
    addAnswerM.mutate({
      text: newText.trim(),
      price: isNaN(priceNum) ? 0 : priceNum,
      weight: 1,
      sort: answers.length,
    });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg">

        {/* ── Header ── */}
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <div className="flex items-center gap-2">
            {isCreateMode && phase === "form" && (
              <Button size="icon" variant="ghost" className="shrink-0 -ml-1" disabled={creating} onClick={() => { setPhase("pick"); setGrpForm({ name: "", text: "", format: "radio", required: true, min: "1", max: "1", maxPerAnswer: "1" }); setPlannedAnswers([]); }} title="Back to templates">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div className="flex-1">
              <DialogTitle>
                {isCreateMode
                  ? phase === "pick" ? "Choose a Template" : "Configure Group"
                  : "Edit Modifier Group"}
              </DialogTitle>
              {isCreateMode && phase === "pick" && (
                <DialogDescription className="text-xs mt-0.5">Select a starting point for your modifier group</DialogDescription>
              )}
              {isCreateMode && phase === "form" && (
                <DialogDescription className="text-xs mt-0.5">Step 2 of 2 — review settings and options, then create</DialogDescription>
              )}
              {question && <DialogDescription className="text-xs">ID {question.id}</DialogDescription>}
            </div>
          </div>
        </DialogHeader>

        {/* ── STEP 1: Template gallery (create mode only) ── */}
        {isCreateMode && phase === "pick" && (
          <>
            <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4 space-y-4">
              {TEMPLATE_CATEGORIES.map((cat) => {
                const catTemplates = MODIFIER_TEMPLATES.filter((t) => t.category === cat);
                if (catTemplates.length === 0) return null;
                return (
                  <div key={cat}>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{cat}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {catTemplates.map((tpl) => {
                        const Icon = tpl.icon;
                        return (
                          <button
                            key={tpl.id}
                            className="flex items-start gap-2.5 rounded-md border border-border p-3 text-left bg-card hover-elevate active-elevate-2 transition-colors"
                            onClick={() => selectTemplate(tpl)}
                          >
                            <Icon className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium leading-tight truncate">{tpl.name}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {tpl.format === "radio" ? "Radio" : tpl.format === "stepper" ? "Stepper" : "Checkbox"} · {tpl.required ? "Required" : "Optional"} · {tpl.answers.length} options
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t flex-row justify-between gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button variant="ghost" onClick={startScratch}>
                <PenLine className="w-4 h-4 mr-2" />
                Start from Scratch
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ── STEP 2: Form + planned options (create mode) ── */}
        {isCreateMode && phase === "form" && (
          <>
            <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4 space-y-5">
              {/* Group settings */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b pb-1">Group Settings</p>
                <div className="space-y-1">
                  <Label>Name *</Label>
                  <Input value={grpForm.name} onChange={(e) => setGrpForm((f) => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Display Text</Label>
                  <Input placeholder="Defaults to name if blank" value={grpForm.text} onChange={(e) => setGrpForm((f) => ({ ...f, text: e.target.value }))} />
                </div>
                {/* Format cards */}
                <div className="space-y-2">
                  <Label>Format</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {([
                      { value: "radio", Icon: CircleDot, title: "Single choice", desc: "Picks exactly one" },
                      { value: "checkbox", Icon: CheckSquare, title: "Multiple choice", desc: "Picks up to N" },
                      { value: "stepper", Icon: SlidersHorizontal, title: "Qty per option", desc: "Adds multiples of each" },
                    ] as const).map(({ value, Icon, title, desc }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          setGrpForm((f) => {
                            const newMax = value === "radio" ? "1" : (f.format === "radio" ? "3" : f.max);
                            const newMaxPerAnswer = value === "stepper" && f.maxPerAnswer === "1" ? "3" : f.maxPerAnswer;
                            return { ...f, format: value, max: newMax, maxPerAnswer: newMaxPerAnswer };
                          });
                        }}
                        className={`flex flex-col items-center gap-1 rounded-md border p-3 text-center transition-colors cursor-pointer ${
                          grpForm.format === value
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border bg-background text-muted-foreground hover-elevate"
                        }`}
                      >
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="text-xs font-semibold leading-tight">{title}</span>
                        <span className="text-[10px] text-current opacity-70 leading-tight">{desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Required toggle */}
                <div className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2.5 gap-3">
                  <div>
                    <p className="text-sm font-medium leading-tight">Required</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {grpForm.required ? "At least 1 choice is required" : "All choices are optional"}
                    </p>
                  </div>
                  <Switch
                    checked={grpForm.required}
                    onCheckedChange={(checked) => setGrpForm((f) => ({ ...f, required: checked }))}
                  />
                </div>

                {/* Max choices — only for checkbox / stepper */}
                {grpForm.format !== "radio" && (
                  <div className="space-y-1">
                    <Label>Max choices</Label>
                    <Input
                      type="number"
                      min="2"
                      value={grpForm.max}
                      onChange={(e) => setGrpForm((f) => ({ ...f, max: e.target.value }))}
                    />
                    <p className="text-xs text-muted-foreground">Maximum number of options a customer can select.</p>
                  </div>
                )}

                {/* Max per option — only for stepper */}
                {grpForm.format === "stepper" && (
                  <div className="space-y-1">
                    <Label>Max per option</Label>
                    <Input
                      type="number"
                      min="2"
                      value={grpForm.maxPerAnswer}
                      onChange={(e) => setGrpForm((f) => ({ ...f, maxPerAnswer: e.target.value }))}
                    />
                    <p className="text-xs text-muted-foreground">
                      How many of the same option can be added (e.g. 3 = triple bacon).
                    </p>
                  </div>
                )}

                {/* Live summary */}
                <div className="flex items-start gap-2 rounded-md bg-muted/50 border border-border px-3 py-2">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground italic">
                    {configSummary(grpForm.format, grpForm.required, grpForm.max, grpForm.maxPerAnswer)}
                  </p>
                </div>
              </div>

              {/* Planned options */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b pb-1">
                  Planned Options ({plannedAnswers.length})
                </p>
                {plannedAnswers.length === 0 && (
                  <p className="text-xs text-muted-foreground">No options yet. Add some below.</p>
                )}
                {plannedAnswers.map((ans) => (
                  <div key={ans.id} className="flex items-center gap-2">
                    <Input
                      className="h-8 text-sm flex-1"
                      value={ans.text}
                      onChange={(e) => updatePlannedRow(ans.id, "text", e.target.value)}
                      placeholder="Option name"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      className="h-8 text-sm w-24 shrink-0"
                      value={ans.price}
                      onChange={(e) => updatePlannedRow(ans.id, "price", e.target.value)}
                      placeholder="Price"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 shrink-0 text-muted-foreground"
                      onClick={() => removePlannedRow(ans.id)}
                      title="Remove option"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                {/* Add row */}
                <div className="flex items-center gap-2 border-t pt-2">
                  <Input
                    className="h-8 text-sm flex-1"
                    placeholder="New option name"
                    value={addRowText}
                    onChange={(e) => setAddRowText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addPlannedRow(); }}
                  />
                  <Input
                    type="number"
                    step="0.01"
                    className="h-8 text-sm w-24 shrink-0"
                    placeholder="Price"
                    value={addRowPrice}
                    onChange={(e) => setAddRowPrice(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addPlannedRow(); }}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 shrink-0"
                    disabled={!addRowText.trim()}
                    onClick={addPlannedRow}
                    title="Add option"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t flex-row justify-between gap-2">
              <Button variant="outline" onClick={onClose} disabled={creating}>Cancel</Button>
              <Button
                onClick={doCreate}
                disabled={!grpForm.name.trim() || creating}
              >
                {creating && createProgress
                  ? `Creating options ${createProgress.done}/${createProgress.total}…`
                  : creating
                  ? "Creating…"
                  : plannedAnswers.filter((a) => a.text.trim()).length > 0
                  ? `Create Group + ${plannedAnswers.filter((a) => a.text.trim()).length} Options`
                  : "Create Group"}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ── Edit mode ── */}
        {!isCreateMode && (
          <>
            <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4 space-y-5">
              {/* Group Settings */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b pb-1">Group Settings</p>
                <div className="space-y-1"><Label>Name *</Label>
                  <Input value={grpForm.name} onChange={(e) => setGrpForm((f) => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="space-y-1"><Label>Display Text</Label>
                  <Input placeholder="Defaults to name if blank" value={grpForm.text} onChange={(e) => setGrpForm((f) => ({ ...f, text: e.target.value }))} />
                </div>
                {/* Format/min/max are read-only — the API does not support updating them via this endpoint */}
                <div className="space-y-1">
                  <Label className="text-muted-foreground">Format <span className="text-xs font-normal">(read-only after creation)</span></Label>
                  <p className="text-sm font-medium">{formatLabel(grpForm.format)}</p>
                  <p className="text-xs text-muted-foreground">
                    {configSummary(grpForm.format, grpForm.required, grpForm.max, grpForm.maxPerAnswer)}
                  </p>
                </div>
                <Button size="sm" onClick={() => updateGrpM.mutate()} disabled={!grpForm.name.trim() || updateGrpM.isPending}>
                  {updateGrpM.isPending ? "Saving…" : "Save Settings"}
                </Button>
              </div>

              {/* Options / Answers */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b pb-1">
                  Options ({answers.length})
                </p>
                {answers.length === 0 && (
                  <p className="text-xs text-muted-foreground">No options yet. Add one below.</p>
                )}
                {answers.map((ans: any) => {
                  const isEditText = editAnswerField === `text-${ans.id}`;
                  const isEditPrice = editAnswerField === `price-${ans.id}`;
                  const ansIs86d = ans.available === false || ans.status === "deleted" || ans.status === "inactive";
                  const busy = updateAnswerM.isPending || deleteAnswerM.isPending || restoreAnswerM.isPending;

                  return (
                    <div key={ans.id} className={`flex items-start gap-2 px-3 py-2 rounded-md border bg-card ${ansIs86d ? "opacity-60 border-orange-200 dark:border-orange-900/40" : "border-border"}`}>
                      <div className="flex-1 min-w-0 space-y-0.5">
                        {isEditText ? (
                          <Input value={editAnswerValue} autoFocus className="h-7 text-sm"
                            onChange={(e) => setEditAnswerValue(e.target.value)}
                            onBlur={() => saveAnswerInline(ans)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); saveAnswerInline(ans); }
                              if (e.key === "Escape") setEditAnswerField(null);
                            }}
                          />
                        ) : (
                          <button className={`text-sm text-left truncate w-full hover:text-primary ${ansIs86d ? "line-through text-muted-foreground" : ""}`}
                            onClick={() => { if (!ansIs86d) { setEditAnswerField(`text-${ans.id}`); setEditAnswerValue(ans.text ?? ""); } }}
                            title={ansIs86d ? "86'd option" : "Click to edit name"}>
                            {ans.text || <span className="text-muted-foreground italic">Unnamed</span>}
                            {ansIs86d && <span className="ml-1 text-xs no-underline not-italic">(86'd)</span>}
                          </button>
                        )}
                      </div>
                      <div className="shrink-0 w-20 pt-0.5">
                        {!ansIs86d && (isEditPrice ? (
                          <Input type="number" step="0.01" value={editAnswerValue} autoFocus className="h-7 text-sm"
                            placeholder="0.00"
                            onChange={(e) => setEditAnswerValue(e.target.value)}
                            onBlur={() => saveAnswerInline(ans)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); saveAnswerInline(ans); }
                              if (e.key === "Escape") setEditAnswerField(null);
                            }}
                          />
                        ) : (
                          <button className="text-xs text-muted-foreground text-right block w-full hover:text-primary"
                            onClick={() => { const p = ans.prices?.base ?? ans.prices?.default ?? ans.price; setEditAnswerField(`price-${ans.id}`); setEditAnswerValue(p != null ? String(p) : ""); }}
                            title="Click to edit price">
                            {(() => { const p = ans.prices?.base ?? ans.prices?.default ?? ans.price; return p != null && p !== 0 ? `$${Number(p).toFixed(2)}` : <span className="italic">+ price</span>; })()}
                          </button>
                        ))}
                      </div>
                      {ansIs86d ? (
                        <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0 text-primary mt-0.5"
                          disabled={busy} title="Restore option"
                          onClick={() => restoreAnswerM.mutate(ans.id)}>
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                      ) : (
                        <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0 text-orange-500 mt-0.5"
                          disabled={busy} title="86 this option"
                          onClick={() => setEightySixAnswerTarget(ans)}>
                          <Ban className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  );
                })}

                {/* Add new option */}
                <div className="border-t pt-3 space-y-2">
                  <p className="text-xs text-muted-foreground font-medium">Add option</p>
                  <div className="flex gap-2">
                    <Input placeholder="Option name *" value={newText}
                      onChange={(e) => setNewText(e.target.value)}
                      className="h-8 text-sm flex-1"
                      onKeyDown={(e) => { if (e.key === "Enter") doAddAnswer(); }}
                    />
                    <Input type="number" step="0.01" placeholder="Price" value={newPrice}
                      onChange={(e) => setNewPrice(e.target.value)}
                      className="h-8 text-sm w-24"
                      onKeyDown={(e) => { if (e.key === "Enter") doAddAnswer(); }}
                    />
                    <Button size="sm" className="h-8 shrink-0"
                      disabled={!newText.trim() || addAnswerM.isPending}
                      onClick={doAddAnswer}>
                      <Plus className="w-3 h-3 mr-1" />
                      {addAnswerM.isPending ? "…" : "Add"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t">
              <Button variant="outline" onClick={onClose}>Close</Button>
            </DialogFooter>
          </>
        )}

      </DialogContent>
      <EightySixModal
        open={!!eightySixAnswerTarget}
        item={eightySixAnswerTarget}
        onClose={() => setEightySixAnswerTarget(null)}
        onConfirm={(body) => { if (eightySixAnswerTarget) deleteAnswerM.mutate({ answerId: eightySixAnswerTarget.id, body }); }}
        isPending={deleteAnswerM.isPending}
      />
    </Dialog>
  );
}

// ─── DRAGGABLE MODIFIER ROW (right panel) ────────────────────────────────────

function DraggableModifierRow({
  question, isLinked, linkConfig, hasExpandedItem, linkPending, unlinkPending,
  onLink, onUnlink, onLinkConfigChange, onEdit,
}: {
  question: any; isLinked: boolean;
  linkConfig: { required: boolean; order: string };
  hasExpandedItem: boolean;
  linkPending: boolean; unlinkPending: boolean;
  onLink: () => void; onUnlink: () => void;
  onLinkConfigChange: (cfg: { required: boolean; order: string }) => void;
  onEdit: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `mod-${question.id}`,
    data: { type: "modifier", questionId: question.id },
  });

  const rawAnswers: any[] = Array.isArray(question.answers) ? question.answers
    : Array.isArray(question.answer) ? question.answer : [];
  const answers: any[] = [...rawAnswers].sort((a: any, b: any) => (a.sort ?? 0) - (b.sort ?? 0));

  return (
    <div
      ref={setNodeRef}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      className={`rounded-md border p-3 space-y-2 transition-colors ${isLinked ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}
    >
      <div className="flex items-start gap-2">
        <button {...listeners} {...attributes}
          className="text-muted-foreground cursor-grab active:cursor-grabbing mt-0.5 shrink-0 touch-none"
          aria-label="Drag to link to item">
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{question.name}</span>
            {isLinked && <Badge variant="default" className="text-xs shrink-0">Linked</Badge>}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {formatLabel(question.format)} · {answers.length} option{answers.length !== 1 ? "s" : ""}
          </p>
        </div>
        <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0 mt-0.5" onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit group & options">
          <Settings className="w-3 h-3" />
        </Button>
        {hasExpandedItem && (
          isLinked ? (
            <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0 mt-0.5" onClick={onUnlink} disabled={unlinkPending} title="Unlink from item">
              <Unlink2 className="w-3 h-3 text-destructive" />
            </Button>
          ) : (
            <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0 mt-0.5" onClick={onLink} disabled={linkPending} title="Link to item">
              <Link2 className="w-3 h-3 text-primary" />
            </Button>
          )
        )}
      </div>

      {hasExpandedItem && isLinked && (
        <div className="flex items-center gap-3 flex-wrap pt-1">
          <div className="flex items-center gap-1.5">
            <Switch checked={linkConfig.required} onCheckedChange={(v) => onLinkConfigChange({ ...linkConfig, required: v })}
              id={`rp-req-${question.id}`} className="scale-90" />
            <label htmlFor={`rp-req-${question.id}`} className="text-xs cursor-pointer">Required</label>
          </div>
          <Input type="number" value={linkConfig.order}
            onChange={(e) => onLinkConfigChange({ ...linkConfig, order: e.target.value })}
            placeholder="Sort order" className="h-7 w-24 text-xs" />
        </div>
      )}
    </div>
  );
}

// ─── ITEM ACCORDION CONTENT (inline expand panel) ────────────────────────────

type AccordionProps = {
  item: any;
  isLoadingDetail: boolean;
  categories: any[];
  restaurantId: string;
  menuId: string;
  token: string;
  allQuestions: any[];
  allAnswers: any[];
  linkedIds: number[];
  linkConfigs: Record<string, { required: boolean; order: string }>;
  onLinkConfigChange: (qId: number, cfg: { required: boolean; order: string }) => void;
  linkPending: boolean;
  unlinkPending: boolean;
  onLink: (qId: number) => void;
  onUnlink: (qId: number) => void;
  openDetailsSignal?: number;
  onImageUpload?: (file: File) => void;
  uploadingFor?: number | null;
};

function ItemAccordionContent({
  item, isLoadingDetail, categories, restaurantId, menuId, token,
  allQuestions, allAnswers, linkedIds, linkConfigs, onLinkConfigChange,
  linkPending, unlinkPending, onLink, onUnlink, openDetailsSignal,
  onImageUpload, uploadingFor,
}: AccordionProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<"details" | "modifiers">("details");
  const [addModOpen, setAddModOpen] = useState(false);
  useEffect(() => { setActiveTab("details"); }, [item.id]);
  useEffect(() => { if (openDetailsSignal) setActiveTab("details"); }, [openDetailsSignal]);
  const [form, setForm] = useState({
    name: "", description: "", category: "",
    base_price: "",
    service: ["takeout", "delivery"] as string[],
    type: "",
    prep_time: "",
  });

  const { data: itemTypesRaw } = useQuery({
    queryKey: ["/api/admin/lam/itemtypes"],
    queryFn: () => lamFetch(`/api/admin/lam/itemtypes`, token),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });
  const itemTypes: { id: number; name: string }[] = Array.isArray(itemTypesRaw)
    ? itemTypesRaw
    : (itemTypesRaw?.item_types ?? itemTypesRaw?.types ?? []);

  function normalizeItemType(raw: string, types: { id: number; name: string }[]): string {
    if (!raw) return "";
    if (types.find((t) => t.name === raw)) return raw;
    const normalized = raw.toLowerCase().replace(/[_-]/g, " ").trim();
    const match = types.find((t) => t.name.toLowerCase().trim() === normalized);
    return match ? match.name : "";
  }

  useEffect(() => {
    if (item) {
      const rawPrices = item.prices ?? {};
      const basePriceVal =
        rawPrices.takeout?.base ??
        rawPrices.base ??
        (typeof rawPrices.takeout === "number" ? rawPrices.takeout : null) ??
        item.price ??
        null;
      // item.preparation is always in minutes here — see effectiveExpandedItem
      // memo which normalises LAM (seconds) to minutes before this form sees it.
      const prepMins = item.preparation != null
        ? String(Math.round(item.preparation))
        : item.prep_time != null ? String(item.prep_time) : "";
      const rawType = item.item_type_name ?? item.type ?? "";
      setForm({
        name: item.name ?? "",
        description: item.description ?? "",
        category: item.category ? String(item.category) : "",
        base_price: basePriceVal != null ? String(basePriceVal) : "",
        service: Array.isArray(item.service) ? item.service : (item.services ?? ["takeout", "delivery"]),
        type: normalizeItemType(rawType, itemTypes),
        prep_time: prepMins,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, item]);

  useEffect(() => {
    if (itemTypes.length === 0) return;
    setForm((f) => {
      const normalized = normalizeItemType(f.type, itemTypes);
      if (normalized === f.type) return f;
      return { ...f, type: normalized };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemTypes.length]);

  const updateMutation = useMutation({
    mutationFn: (data: any) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/menus/${menuId}/items/${item?.id}`, token, {
        method: "POST", body: JSON.stringify(data),
      }),
    onSuccess: () => {
      toast({ title: "Item saved" });
      qc.invalidateQueries({ queryKey: ["/api/admin/lam/restaurants", restaurantId, "menus"] });
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  function save() {
    if (!form.name.trim()) return;
    const basePrice =
      form.base_price !== "" && !isNaN(Number(form.base_price))
        ? Number(form.base_price)
        : item
        ? itemRequiredFields(item).price
        : 0;
    updateMutation.mutate({
      name: form.name.trim(),
      description: form.description,
      price: basePrice,
      service: form.service,
      sort: item?.sort ?? item?.sort_order ?? 0,
      ...(form.type ? { type: form.type } : {}),
      category: form.category ? Number(form.category) : undefined,
      ...(Number(form.prep_time) > 0 && { preparation: Number(form.prep_time) }),
    });
  }

  const linkedQs = allQuestions
    .filter((q: any) => linkedIds.includes(q.id))
    .sort((a: any, b: any) => {
      const cfgA = linkConfigs[`${item?.id}-${a.id}`];
      const cfgB = linkConfigs[`${item?.id}-${b.id}`];
      const reqA = cfgA?.required ?? a.required ?? false;
      const reqB = cfgB?.required ?? b.required ?? false;
      if (reqA !== reqB) return reqB ? 1 : -1;
      const ordA = Number(cfgA?.order ?? a.order ?? 0);
      const ordB = Number(cfgB?.order ?? b.order ?? 0);
      return ordA - ordB;
    });
  const unlinkedQs = allQuestions.filter((q: any) => !linkedIds.includes(q.id));

  if (!item) return null;

  return (
    <div className="border-t pt-3 px-3 pb-4">
      {/* Tab headers */}
      <div className="flex gap-0 border-b mb-3">
        {(["details", "modifiers"] as const).map((tab) => (
          <button
            key={tab}
            className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors capitalize ${
              activeTab === tab
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "modifiers" && linkedIds.length > 0
              ? `Modifiers (${linkedIds.length})`
              : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Details tab */}
      {activeTab === "details" && (
        <div className="space-y-3 pb-12">
          <div className="space-y-1"><Label>Name *</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          {/* Row: Base Price (left) | Category (right) */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Base Price</Label>
              <Input type="number" step="0.01" placeholder="—"
                value={form.base_price}
                onChange={(e) => setForm((f) => ({ ...f, base_price: e.target.value }))} />
              {isLoadingDetail ? (
                <div className="h-4 w-32 rounded bg-muted animate-pulse" />
              ) : (() => {
                const rawPrices = item?.prices ?? {};
                const takeoutRetail = rawPrices.takeout?.retail ?? null;
                const deliveryRetail = rawPrices.delivery?.retail ?? null;
                if (takeoutRetail == null && deliveryRetail == null) return null;
                return (
                  <div className="space-y-0.5 text-xs text-muted-foreground pt-0.5">
                    {takeoutRetail != null && <div>Takeout: <strong>${Number(takeoutRetail).toFixed(2)}</strong></div>}
                    {deliveryRetail != null && <div>Delivery: <strong>${Number(deliveryRetail).toFixed(2)}</strong></div>}
                    <div className="opacity-60 leading-tight">Set by Eatzy — not editable here.</div>
                  </div>
                );
              })()}
            </div>
            <div className="space-y-1"><Label>Category</Label>
              <Select value={form.category || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, category: v === "__none__" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {categories.map((c: any) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1"><Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
          </div>
          <div className="space-y-1">
            <Label>Services</Label>
            <div className="flex flex-wrap gap-3">
              {["takeout", "delivery", "catering"].map((svc) => (
                <label key={svc} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.service.includes(svc)}
                    onChange={() => setForm((f) => ({
                      ...f,
                      service: f.service.includes(svc) ? f.service.filter((s) => s !== svc) : [...f.service, svc],
                    }))} />
                  {svc}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Prep Time (min)</Label>
              <Input type="number" value={form.prep_time} onChange={(e) => setForm((f) => ({ ...f, prep_time: e.target.value }))} placeholder="—" />
            </div>
            <div className="space-y-1"><Label>Item Type</Label>
              {itemTypes.length > 0 ? (
                <Select
                  value={form.type || "__none__"}
                  onValueChange={(v) => setForm((f) => ({ ...f, type: v === "__none__" ? "" : v }))}
                >
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {itemTypes.map((t: any) => (
                      <SelectItem key={t.name ?? t.id} value={t.name ?? String(t.id)}>
                        {t.name ?? t.label ?? String(t.id)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} placeholder="e.g. food" />
              )}
            </div>
          </div>
          {/* Sticky footer — always visible without scrolling */}
          <div className="sticky bottom-0 flex items-center justify-between gap-2 pt-2 pb-1 -mx-3 px-3 bg-background border-t">
            {onImageUpload && (
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onImageUpload(f); e.target.value = ""; }} />
                <Button type="button" variant="outline" size="sm" asChild>
                  <span>
                    {uploadingFor === item?.id
                      ? <><RefreshCw className="w-3 h-3 mr-1.5 animate-spin" /> Uploading…</>
                      : <><ImageIcon className="w-3 h-3 mr-1.5" /> {item?.media?.logo || item?.media?.thumbnail || item?.media?.item_thumbnail ? "Change Image" : "Upload Image"}</>
                    }
                  </span>
                </Button>
              </label>
            )}
            <Button onClick={save} disabled={!form.name.trim() || updateMutation.isPending} size="sm" className="ml-auto">
              {updateMutation.isPending ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </div>
      )}

      {/* Modifiers tab */}
      {activeTab === "modifiers" && (
        <div className="space-y-3">
          {linkedQs.length === 0 && (
            <p className="text-sm text-muted-foreground">No modifier groups linked. Use the Add button below, or drag from the modifier panel.</p>
          )}
          {linkedQs.map((q: any) => {
            const cfgKey = `${item.id}-${q.id}`;
            const cfg = linkConfigs[cfgKey] ?? { required: false, order: "" };
            const answers: any[] = Array.isArray(q.answers) ? q.answers
              : Array.isArray(q.answer) ? q.answer
              : allAnswers.filter((a: any) => a.parent === q.id);
            return (
              <div key={q.id} className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium">{q.name}</p>
                    <p className="text-xs text-muted-foreground">{formatLabel(q.format)} · {answers.length} option{answers.length !== 1 ? "s" : ""}</p>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0"
                    onClick={() => onUnlink(q.id)} disabled={unlinkPending}>
                    <Unlink2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <Switch checked={cfg.required} onCheckedChange={(v) => onLinkConfigChange(q.id, { ...cfg, required: v })}
                      id={`acc-req-${item.id}-${q.id}`} className="scale-90" />
                    <label htmlFor={`acc-req-${item.id}-${q.id}`} className="text-xs cursor-pointer">Required</label>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-muted-foreground">Order</label>
                    <Input type="number" value={cfg.order} onChange={(e) => onLinkConfigChange(q.id, { ...cfg, order: e.target.value })}
                      placeholder="0" className="h-7 w-20 text-xs" />
                  </div>
                </div>
              </div>
            );
          })}
          {unlinkedQs.length > 0 && (
            <Popover open={addModOpen} onOpenChange={setAddModOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="w-full">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add modifier group
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-80" align="start">
                <Command>
                  <CommandInput placeholder="Search modifier groups…" />
                  <CommandList className="max-h-52">
                    <CommandEmpty>No modifier groups found.</CommandEmpty>
                    <CommandGroup>
                      {unlinkedQs.map((q: any) => (
                        <CommandItem
                          key={q.id}
                          value={q.name}
                          onSelect={() => {
                            onLink(q.id);
                            setAddModOpen(false);
                          }}
                          disabled={linkPending}
                        >
                          <span className="flex-1 truncate">{q.name}</span>
                          <span className="text-xs text-muted-foreground ml-2 shrink-0">{formatLabel(q.format)}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}
    </div>
  );
}

// ─── BULK PRICE TABLE ─────────────────────────────────────────────────────────

function getItemBasePrice(item: any): number | null {
  if (item.price != null) return Number(item.price);
  return null;
}

function getItemSvcPrice(item: any, service: string): number | null {
  const prices = Array.isArray(item.prices) ? item.prices : [];
  const p = prices.find((p: any) => p.service === service);
  return p?.price != null ? Number(p.price) : null;
}

type BulkPriceCols = "base" | "takeout" | "delivery" | "catering";

export type BulkPriceTableHandle = {
  getDirtyRows: () => Array<{ itemId: number; prices: { base?: number | null; takeout?: number | null; delivery?: number | null; catering?: number | null } }>;
  clearDirty: (ids?: number[]) => void;
};

const BulkPriceTable = forwardRef<BulkPriceTableHandle, {
  items: any[];
  categories: any[];
  onSaveRow: (itemId: number, prices: { base?: number | null; takeout?: number | null; delivery?: number | null; catering?: number | null }) => Promise<boolean>;
  savingIds: Set<number>;
  failedIds: number[];
  selectedIds?: Set<number>;
  onToggleSelected?: (itemId: number) => void;
  showCheckboxes?: boolean;
}>(function BulkPriceTable({
  items, categories, onSaveRow, savingIds, failedIds,
  selectedIds, onToggleSelected, showCheckboxes,
}, ref) {
  type RowVal = { base: string; takeout: string; delivery: string; catering: string };

  function makeRowVal(it: any): RowVal {
    return {
      base: getItemBasePrice(it) != null ? String(getItemBasePrice(it)) : "",
      takeout: getItemSvcPrice(it, "takeout") != null ? String(getItemSvcPrice(it, "takeout")) : "",
      delivery: getItemSvcPrice(it, "delivery") != null ? String(getItemSvcPrice(it, "delivery")) : "",
      catering: getItemSvcPrice(it, "catering") != null ? String(getItemSvcPrice(it, "catering")) : "",
    };
  }

  const [rows, setRows] = useState<Record<number, RowVal>>(() => {
    const m: Record<number, RowVal> = {};
    items.forEach((it: any) => { m[it.id] = makeRowVal(it); });
    return m;
  });
  const [dirty, setDirty] = useState<Set<number>>(new Set());

  useEffect(() => {
    setRows((prev) => {
      const next: Record<number, RowVal> = {};
      items.forEach((it: any) => { next[it.id] = prev[it.id] ?? makeRowVal(it); });
      return next;
    });
  }, [items]);

  const hasTakeout = items.some((it: any) => getItemSvcPrice(it, "takeout") != null);
  const hasDelivery = items.some((it: any) => getItemSvcPrice(it, "delivery") != null);
  const hasCatering = items.some((it: any) => getItemSvcPrice(it, "catering") != null);

  // Build ordered list of [rowIndex, col] pairs for tab navigation
  const colKeys: BulkPriceCols[] = ["base", ...(hasTakeout ? ["takeout" as BulkPriceCols] : []), ...(hasDelivery ? ["delivery" as BulkPriceCols] : []), ...(hasCatering ? ["catering" as BulkPriceCols] : [])];
  const numCols = colKeys.length;
  const inputRefs = useRef<Array<Array<HTMLInputElement | null>>>([]);

  // Expose getDirtyRows + clearDirty to parent via ref
  useImperativeHandle(ref, () => ({
    getDirtyRows: () => {
      const result: Array<{ itemId: number; prices: { base?: number | null; takeout?: number | null; delivery?: number | null; catering?: number | null } }> = [];
      dirty.forEach((itemId) => {
        const prices = buildPrices(itemId);
        if (Object.keys(prices).length > 0) result.push({ itemId, prices });
      });
      return result;
    },
    clearDirty: (ids?: number[]) => {
      setDirty((prev) => {
        if (!ids) return new Set();
        const s = new Set(prev);
        ids.forEach((id) => s.delete(id));
        return s;
      });
    },
  }), [rows, dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  function getOriginalVal(item: any, col: BulkPriceCols): number | null {
    if (col === "base") return getItemBasePrice(item);
    return getItemSvcPrice(item, col as "takeout" | "delivery" | "catering");
  }

  function setCell(itemId: number, col: BulkPriceCols, val: string, item: any) {
    setRows((prev) => ({ ...prev, [itemId]: { ...prev[itemId], [col]: val } }));
    // Mark dirty if any cell differs from original (including cleared cells when original was set)
    setDirty((prev) => {
      const s = new Set(prev);
      const row = { ...rows[itemId], [col]: val };
      const anyDirty = colKeys.some((k) => {
        const cellVal = row[k];
        const orig = getOriginalVal(item, k);
        const num = parseFloat(cellVal);
        // Empty cell with an original value = clearing the price (dirty)
        if (cellVal.trim() === "" && orig != null) return true;
        // Numeric value that differs from original
        return !isNaN(num) && num !== orig;
      });
      if (anyDirty) s.add(itemId); else s.delete(itemId);
      return s;
    });
  }

  function buildPrices(itemId: number): { base?: number | null; takeout?: number | null; delivery?: number | null; catering?: number | null } {
    const row = rows[itemId];
    const prices: { base?: number | null; takeout?: number | null; delivery?: number | null; catering?: number | null } = {};
    if (!row) return prices;
    const addField = (key: BulkPriceCols, colName: keyof typeof prices, colEnabled: boolean) => {
      if (!colEnabled) return;
      const cellVal = row[key].trim();
      const num = parseFloat(cellVal);
      if (cellVal === "") prices[colName] = null; // explicit clear
      else if (!isNaN(num)) prices[colName] = num;
    };
    addField("base", "base", true);
    addField("takeout", "takeout", hasTakeout);
    addField("delivery", "delivery", hasDelivery);
    addField("catering", "catering", hasCatering);
    return prices;
  }

  function handleSaveRow(itemId: number) {
    if (!dirty.has(itemId)) return;
    const prices = buildPrices(itemId);
    if (Object.keys(prices).length === 0) return;
    void onSaveRow(itemId, prices).then((ok) => {
      if (ok) setDirty((prev) => { const s = new Set(prev); s.delete(itemId); return s; });
    });
  }

  function focusCell(rowIdx: number, colIdx: number) {
    const ref = inputRefs.current[rowIdx]?.[colIdx];
    if (ref) { ref.focus(); ref.select(); }
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLInputElement>, rowIdx: number, colIdx: number, itemId: number) {
    if (e.key === "Enter") { e.preventDefault(); handleSaveRow(itemId); }
    if (e.key === "Tab") {
      e.preventDefault();
      const totalCells = items.length * numCols;
      const flatIdx = rowIdx * numCols + colIdx + (e.shiftKey ? -1 : 1);
      const wrapped = ((flatIdx % totalCells) + totalCells) % totalCells;
      const nextRow = Math.floor(wrapped / numCols);
      const nextCol = wrapped % numCols;
      focusCell(nextRow, nextCol);
    }
  }

  const cellClass = "w-20 h-7 text-right text-xs border border-input rounded-md px-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-primary";

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="border-b bg-muted/30">
            {showCheckboxes && <th className="w-6 py-1.5 px-2" />}
            <th className="py-1.5 px-2 text-left font-medium text-muted-foreground">Name</th>
            <th className="py-1.5 px-2 text-right font-medium text-muted-foreground w-24">Base $</th>
            {hasTakeout && <th className="py-1.5 px-2 text-right font-medium text-muted-foreground w-24">Takeout $</th>}
            {hasDelivery && <th className="py-1.5 px-2 text-right font-medium text-muted-foreground w-24">Delivery $</th>}
            {hasCatering && <th className="py-1.5 px-2 text-right font-medium text-muted-foreground w-24">Catering $</th>}
            <th className="py-1.5 px-2 text-left font-medium text-muted-foreground w-28">Category</th>
            <th className="py-1.5 px-2 w-8" />
          </tr>
        </thead>
        <tbody>
          {items.map((item: any, rowIdx) => {
            const row = rows[item.id] ?? makeRowVal(item);
            const isDirty = dirty.has(item.id);
            const isSaving = savingIds.has(item.id);
            const isFailed = failedIds.includes(item.id);
            const catName = categories.find((c: any) => c.id === item.category)?.name ?? "";
            const isDeleted = item.status === "deleted" || item.status === "inactive";
            if (!inputRefs.current[rowIdx]) inputRefs.current[rowIdx] = [];
            return (
              <tr key={item.id}
                className={`border-b last:border-0 transition-colors ${isDeleted ? "opacity-40" : ""} ${isFailed ? "bg-destructive/5" : isDirty ? "bg-primary/5" : "hover:bg-muted/30"}`}
              >
                {showCheckboxes && (
                  <td className="py-1 px-2">
                    <input type="checkbox"
                      checked={selectedIds?.has(item.id) ?? false}
                      onChange={() => onToggleSelected?.(item.id)}
                      className="rounded"
                    />
                  </td>
                )}
                <td className="py-1 px-2 font-medium">
                  <span className="flex items-center gap-1 truncate max-w-[150px]">
                    {isFailed && <AlertCircle className="w-3 h-3 text-destructive shrink-0" />}
                    {item.name}
                  </span>
                </td>
                {/* Base price */}
                <td className="py-1 px-2 text-right">
                  <input
                    ref={(el) => { inputRefs.current[rowIdx][0] = el; }}
                    type="number" step="0.01" min="0"
                    value={row.base}
                    disabled={isDeleted}
                    onChange={(e) => setCell(item.id, "base", e.target.value, item)}
                    onKeyDown={(e) => handleKeyDown(e, rowIdx, 0, item.id)}
                    className={cellClass}
                  />
                </td>
                {/* Takeout */}
                {hasTakeout && (
                  <td className="py-1 px-2 text-right">
                    <input
                      ref={(el) => { inputRefs.current[rowIdx][colKeys.indexOf("takeout")] = el; }}
                      type="number" step="0.01" min="0"
                      value={row.takeout}
                      disabled={isDeleted}
                      onChange={(e) => setCell(item.id, "takeout", e.target.value, item)}
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, colKeys.indexOf("takeout"), item.id)}
                      className={cellClass}
                    />
                  </td>
                )}
                {/* Delivery */}
                {hasDelivery && (
                  <td className="py-1 px-2 text-right">
                    <input
                      ref={(el) => { inputRefs.current[rowIdx][colKeys.indexOf("delivery")] = el; }}
                      type="number" step="0.01" min="0"
                      value={row.delivery}
                      disabled={isDeleted}
                      onChange={(e) => setCell(item.id, "delivery", e.target.value, item)}
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, colKeys.indexOf("delivery"), item.id)}
                      className={cellClass}
                    />
                  </td>
                )}
                {/* Catering */}
                {hasCatering && (
                  <td className="py-1 px-2 text-right">
                    <input
                      ref={(el) => { inputRefs.current[rowIdx][colKeys.indexOf("catering")] = el; }}
                      type="number" step="0.01" min="0"
                      value={row.catering}
                      disabled={isDeleted}
                      onChange={(e) => setCell(item.id, "catering", e.target.value, item)}
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, colKeys.indexOf("catering"), item.id)}
                      className={cellClass}
                    />
                  </td>
                )}
                <td className="py-1 px-2 text-muted-foreground">
                  <span className="block truncate max-w-[100px]">{catName}</span>
                </td>
                <td className="py-1 px-2">
                  <button
                    onClick={() => handleSaveRow(item.id)}
                    disabled={(!isDirty && !isFailed) || isSaving}
                    title={isFailed ? "Retry save" : isDirty ? "Save row" : "No changes"}
                    className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
                      isFailed ? "text-destructive hover:bg-destructive/10" :
                      isDirty && !isSaving ? "text-primary hover:bg-primary/10" :
                      "text-muted-foreground/30 cursor-not-allowed"
                    }`}
                  >
                    {isSaving
                      ? <RefreshCw className="w-3 h-3 animate-spin" />
                      : isFailed
                        ? <RefreshCw className="w-3 h-3" />
                        : <Check className="w-3 h-3" />
                    }
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});

// ─── ADJUST PRICES DIALOG ─────────────────────────────────────────────────────

type AdjustScope = "all" | "category" | "selected";
type AdjustType = "percent" | "flat";
type AdjustRounding = "none" | "cent" | "5cent" | "10cent" | "25cent" | "dollar";
type AdjustPriceLevel = "base" | "takeout" | "delivery" | "catering";

function roundPrice(price: number, rounding: AdjustRounding): number {
  switch (rounding) {
    case "none": return Math.round(price * 100) / 100;
    case "cent": return Math.round(price * 100) / 100;
    case "5cent": return Math.round(price * 20) / 20;
    case "10cent": return Math.round(price * 10) / 10;
    case "25cent": return Math.round(price * 4) / 4;
    case "dollar": return Math.round(price);
    default: return Math.round(price * 100) / 100;
  }
}

function AdjustPricesDialog({
  open, onClose,
  items, categories, selectedCatId, selectedIds,
  onSaveOne,
  onDone,
  restaurantId,
  token,
  hasBulkSelection,
  onEnterBulkEdit,
}: {
  open: boolean;
  onClose: () => void;
  items: any[];
  categories: any[];
  selectedCatId: number | null;
  selectedIds: Set<number>;
  onSaveOne: (itemId: number, prices: { base?: number; takeout?: number; delivery?: number; catering?: number }) => Promise<boolean>;
  onDone: () => void;
  restaurantId: string | number;
  token: string;
  hasBulkSelection: boolean;
  onEnterBulkEdit: () => void;
}) {
  type ApplyPhase = "config" | "applying" | "done";
  const [phase, setPhase] = useState<ApplyPhase>("config");
  const [progressTotal, setProgressTotal] = useState(0);
  const [progressDone, setProgressDone] = useState(0);
  const [failedItems, setFailedItems] = useState<Array<{ itemId: number; name: string; prices: { base?: number; takeout?: number; delivery?: number; catering?: number } }>>([]);
  const [savedCount, setSavedCount] = useState(0);
  const { toast } = useToast();

  const [scope, setScope] = useState<AdjustScope>("all");
  const [adjustType, setAdjustType] = useState<AdjustType>("percent");
  const [sign, setSign] = useState<"+" | "-">("+");
  const [value, setValue] = useState("10");
  const [rounding, setRounding] = useState<AdjustRounding>("cent");
  const [priceLevels, setPriceLevels] = useState<Set<AdjustPriceLevel>>(new Set<AdjustPriceLevel>(["base"]));

  // Fetch restaurant rounding config and default the rounding selector
  const { data: roundingConfig } = useQuery<any>({
    queryKey: ["/api/admin/lam/restaurants", restaurantId, "rounding"],
    queryFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/rounding`, token),
    enabled: open,
    staleTime: 60_000,
  });

  // When rounding config loads, default the rounding selector to match restaurant base rule.
  // The round_above threshold is applied dynamically in applyRounding(), not the selector.
  useEffect(() => {
    if (!roundingConfig?.use_rounding) {
      setRounding("none");
      return;
    }
    const rd = roundingConfig.round_rule;
    if (rd === "last_digit") {
      // last_digit is the digit prices should end in (e.g. 9 = ¢, 5 = 5¢, 0 = 10¢)
      const digit = Number(roundingConfig.last_digit);
      if (digit === 0) setRounding("10cent");      // end in .X0 → nearest 10 cents
      else if (digit === 5) setRounding("5cent");  // end in .X5 → nearest 5 cents
      else setRounding("cent");                    // end in .X9 or other → nearest cent
    }
    // round_above: selector still shows the base digit rule;
    // applyRounding() applies dollar rounding above the threshold automatically
  }, [roundingConfig]);

  const hasTakeout = items.some((it: any) => getItemSvcPrice(it, "takeout") != null);
  const hasDelivery = items.some((it: any) => getItemSvcPrice(it, "delivery") != null);
  const hasCatering = items.some((it: any) => getItemSvcPrice(it, "catering") != null);

  function toggleLevel(level: AdjustPriceLevel) {
    setPriceLevels((prev) => {
      const s = new Set(prev);
      if (s.has(level)) { if (s.size > 1) s.delete(level); } else s.add(level);
      return s;
    });
  }

  const affectedItems = items.filter((it: any) => {
    if (it.status === "deleted" || it.status === "inactive") return false;
    if (scope === "all") return true;
    if (scope === "category") return it.category === selectedCatId;
    if (scope === "selected") return selectedIds.has(it.id);
    return true;
  });

  const num = parseFloat(value);
  const validNum = !isNaN(num) && num >= 0;

  function applyRounding(price: number): number {
    // If restaurant config has round_above threshold, apply dollar rounding only above threshold
    if (roundingConfig?.use_rounding && roundingConfig.round_above != null) {
      const threshold = Number(roundingConfig.round_above);
      if (price >= threshold) return roundPrice(price, "dollar");
      // Below threshold, fall through to the base last_digit rule (use selected rounding)
    }
    return roundPrice(price, rounding);
  }

  function computeNew(original: number | null): number | null {
    if (original == null || !validNum) return null;
    const delta = adjustType === "percent"
      ? original * (num / 100) * (sign === "+" ? 1 : -1)
      : num * (sign === "+" ? 1 : -1);
    const raw = original + delta;
    if (raw < 0) return null;
    return applyRounding(raw);
  }

  // Example with first affected item
  const exampleItem = affectedItems.find((it: any) => getItemBasePrice(it) != null);
  const exampleOld = exampleItem ? getItemBasePrice(exampleItem) : null;
  const exampleNew = exampleOld != null ? computeNew(exampleOld) : null;

  function buildUpdates(sourceItems: any[]) {
    return sourceItems
      .map((it: any) => {
        const prices: { base?: number; takeout?: number; delivery?: number; catering?: number } = {};
        if (priceLevels.has("base")) {
          const n = computeNew(getItemBasePrice(it));
          if (n != null) prices.base = n;
        }
        if (priceLevels.has("takeout") && hasTakeout) {
          const n = computeNew(getItemSvcPrice(it, "takeout"));
          if (n != null) prices.takeout = n;
        }
        if (priceLevels.has("delivery") && hasDelivery) {
          const n = computeNew(getItemSvcPrice(it, "delivery"));
          if (n != null) prices.delivery = n;
        }
        if (priceLevels.has("catering") && hasCatering) {
          const n = computeNew(getItemSvcPrice(it, "catering"));
          if (n != null) prices.catering = n;
        }
        if (Object.keys(prices).length === 0) return null;
        return { itemId: it.id, name: it.name as string, prices };
      })
      .filter((u): u is { itemId: number; name: string; prices: { base?: number; takeout?: number; delivery?: number; catering?: number } } => u !== null);
  }

  async function runApply(updates: Array<{ itemId: number; name: string; prices: { base?: number; takeout?: number; delivery?: number; catering?: number } }>, prevSavedCount = 0) {
    setPhase("applying");
    setProgressTotal(updates.length);
    setProgressDone(0);
    setSavedCount(prevSavedCount);
    setFailedItems([]);
    let saved = prevSavedCount;
    const failed: typeof failedItems = [];
    for (const u of updates) {
      const ok = await onSaveOne(u.itemId, u.prices);
      if (ok) {
        saved++;
        setSavedCount(saved);
      } else {
        failed.push(u);
      }
      setProgressDone((n) => n + 1);
    }
    setFailedItems(failed);
    setPhase("done");
    if (failed.length === 0) {
      toast({ title: `${saved} item${saved !== 1 ? "s" : ""} updated` });
      onDone();
      onClose();
    }
  }

  function handleApply() {
    const updates = buildUpdates(affectedItems);
    void runApply(updates);
  }

  function handleRetry() {
    void runApply(failedItems, savedCount);
  }

  const roundingOptions: Array<{ value: AdjustRounding; label: string }> = [
    { value: "none", label: "Exact" },
    { value: "cent", label: "¢" },
    { value: "5cent", label: "5¢" },
    { value: "10cent", label: "10¢" },
    { value: "25cent", label: "25¢" },
    { value: "dollar", label: "$1" },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && phase !== "applying" && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust Prices</DialogTitle>
          {phase === "config" && <DialogDescription>Apply a percentage or flat amount change to selected price levels.</DialogDescription>}
          {phase === "applying" && <DialogDescription>Saving changes, please wait…</DialogDescription>}
          {phase === "done" && failedItems.length > 0 && <DialogDescription className="text-destructive">Saved {savedCount}, failed {failedItems.length}. Retry failed items?</DialogDescription>}
        </DialogHeader>

        {/* Progress Phase */}
        {phase === "applying" && (
          <div className="py-6 space-y-4">
            <div className="flex items-center gap-3">
              <RefreshCw className="w-5 h-5 animate-spin text-primary shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">{progressDone} of {progressTotal} items saved</p>
                <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-300"
                    style={{ width: `${progressTotal > 0 ? (progressDone / progressTotal) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Done Phase — failures */}
        {phase === "done" && failedItems.length > 0 && (
          <div className="py-4 space-y-3">
            <div className="rounded-md bg-muted/50 px-3 py-2.5">
              <p className="text-sm font-medium text-green-600 dark:text-green-400">{savedCount} item{savedCount !== 1 ? "s" : ""} updated successfully</p>
              <p className="text-sm text-destructive mt-1">{failedItems.length} item{failedItems.length !== 1 ? "s" : ""} failed:</p>
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {failedItems.map((f) => (
                <div key={f.itemId} className="flex items-center gap-2 text-xs text-destructive px-2">
                  <AlertCircle className="w-3 h-3 shrink-0" />
                  <span className="truncate">{f.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {phase === "config" && <div className="space-y-5 py-2">
          {/* Scope */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Apply to</Label>
            <RadioGroup
              value={scope}
              onValueChange={(v) => {
                const newScope = v as AdjustScope;
                setScope(newScope);
                // Auto-enter bulk edit when "Selected items" is chosen so checkboxes appear
                if (newScope === "selected" && !hasBulkSelection) {
                  onClose();
                  onEnterBulkEdit();
                }
              }}
              className="flex flex-col gap-1.5"
            >
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="all" id="adj-all" />
                <span className="text-sm">All items ({items.filter((it: any) => it.status !== "deleted").length})</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="category" id="adj-cat" />
                <span className="text-sm">
                  {selectedCatId != null
                    ? `This category (${categories.find((c: any) => c.id === selectedCatId)?.name ?? "?"}, ${items.filter((it: any) => it.category === selectedCatId && it.status !== "deleted").length} items)`
                    : "This category (none selected)"}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="selected" id="adj-sel" />
                <span className="text-sm">
                  {hasBulkSelection
                    ? `Selected items (${selectedIds.size})`
                    : "Selected items (enter bulk edit to pick items)"}
                </span>
              </label>
            </RadioGroup>
          </div>

          {/* Price levels */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Price levels</Label>
            <div className="flex flex-wrap gap-2">
              {([
                { key: "base" as AdjustPriceLevel, label: "Base", always: true },
                { key: "takeout" as AdjustPriceLevel, label: "Takeout", always: false },
                { key: "delivery" as AdjustPriceLevel, label: "Delivery", always: false },
                { key: "catering" as AdjustPriceLevel, label: "Catering", always: false },
              ].filter((l) => l.always || (l.key === "takeout" && hasTakeout) || (l.key === "delivery" && hasDelivery) || (l.key === "catering" && hasCatering))).map((l) => (
                <button
                  key={l.key}
                  onClick={() => toggleLevel(l.key)}
                  className={`px-3 py-1 text-xs rounded-md border transition-colors ${priceLevels.has(l.key) ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-input"}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          {/* Adjustment type */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Adjustment</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex rounded-md border overflow-hidden shrink-0">
                <button
                  onClick={() => setSign("+")}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${sign === "+" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                >+</button>
                <button
                  onClick={() => setSign("-")}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${sign === "-" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                >-</button>
              </div>
              <Input
                type="number"
                step={adjustType === "percent" ? "0.1" : "0.01"}
                min="0"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-24 h-9"
              />
              <div className="flex rounded-md border overflow-hidden shrink-0">
                <button
                  onClick={() => setAdjustType("percent")}
                  className={`px-3 py-1.5 text-sm transition-colors flex items-center gap-1 ${adjustType === "percent" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                >
                  <Percent className="w-3 h-3" /> %
                </button>
                <button
                  onClick={() => setAdjustType("flat")}
                  className={`px-3 py-1.5 text-sm transition-colors flex items-center gap-1 ${adjustType === "flat" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                >
                  <DollarSign className="w-3 h-3" /> $
                </button>
              </div>
            </div>
          </div>

          {/* Rounding */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Round to nearest</Label>
            <div className="flex flex-wrap gap-1.5">
              {roundingOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setRounding(opt.value)}
                  className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${rounding === opt.value ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-input"}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="rounded-md bg-muted/50 px-3 py-2.5 space-y-1">
            <p className="text-sm font-medium">
              {affectedItems.length} item{affectedItems.length !== 1 ? "s" : ""} will be updated
              {priceLevels.size > 1 && <span className="text-muted-foreground font-normal"> · {priceLevels.size} price levels each</span>}
            </p>
            {exampleOld != null && exampleNew != null && validNum && (
              <p className="text-xs text-muted-foreground">
                Base example: ${exampleOld.toFixed(2)} → ${exampleNew.toFixed(2)}
                {exampleItem && <span className="ml-1">({exampleItem.name})</span>}
              </p>
            )}
            {!validNum && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Enter a valid amount.</p>}
            {exampleNew != null && exampleNew < 0 && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Result would be negative — those items will be skipped.</p>}
          </div>
        </div>}

        <DialogFooter>
          {phase === "config" && (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={handleApply}
                disabled={!validNum || affectedItems.length === 0 || (scope === "selected" && !hasBulkSelection)}
              >
                Apply to {affectedItems.length} item{affectedItems.length !== 1 ? "s" : ""}
              </Button>
            </>
          )}
          {phase === "applying" && (
            <Button variant="outline" disabled>
              <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Saving…
            </Button>
          )}
          {phase === "done" && failedItems.length > 0 && (
            <>
              <Button variant="outline" onClick={() => { onDone(); onClose(); }}>Close</Button>
              <Button variant="destructive" onClick={handleRetry}>
                Retry {failedItems.length} failed
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── MENUS MANAGE DIALOG ─────────────────────────────────────────────────────

function secondsToTime(s?: number): string {
  if (s == null || isNaN(s)) return "";
  const h = Math.floor(s / 3600).toString().padStart(2, "0");
  const m = Math.floor((s % 3600) / 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

function timeToSeconds(t: string): number {
  const [h = 0, m = 0] = t.split(":").map(Number);
  return h * 3600 + m * 60;
}

const SCHEDULE_DAYS = [
  { key: "mon", label: "M" },
  { key: "tue", label: "T" },
  { key: "wed", label: "W" },
  { key: "thu", label: "T" },
  { key: "fri", label: "F" },
  { key: "sat", label: "S" },
  { key: "sun", label: "S" },
] as const;

const EMPTY_MENU_FORM = {
  name: "",
  type: "daily" as "daily" | "weekly" | "date_range",
  time_start: "",
  time_end: "",
  days: [] as string[],
  date_from: "",
  date_to: "",
};
type MenuForm = typeof EMPTY_MENU_FORM;

function menuToForm(m: any): MenuForm {
  const info = m?.info ?? m;
  const rawType = (info?.type ?? "").toLowerCase();
  const type: MenuForm["type"] = rawType === "weekly" ? "weekly" : rawType === "date_range" ? "date_range" : "daily";

  const times = Array.isArray(info?.available_times) ? info.available_times : [];
  const time_start = times[0]?.start != null ? secondsToTime(times[0].start) : "";
  const time_end = times[0]?.end != null ? secondsToTime(times[0].end) : "";

  const rawDays = info?.available_days ?? "";
  const days = typeof rawDays === "string" && rawDays
    ? rawDays.toLowerCase().split(",").map((d: string) => d.trim()).filter(Boolean)
    : Array.isArray(rawDays) ? rawDays.map((d: string) => d.toLowerCase()) : [];

  return {
    name: info?.name ?? "",
    type,
    time_start,
    time_end,
    days,
    date_from: info?.date_from ?? "",
    date_to: info?.date_to ?? "",
  };
}

function formToPayload(form: MenuForm): Record<string, any> {
  const payload: Record<string, any> = { name: form.name, type: form.type };
  if (form.time_start && form.time_end) {
    payload.available_times = [{ start: timeToSeconds(form.time_start), end: timeToSeconds(form.time_end) }];
  }
  if (form.type === "weekly" && form.days.length) {
    payload.available_days = form.days.join(",");
  }
  if (form.type === "date_range") {
    if (form.date_from) payload.date_from = form.date_from;
    if (form.date_to) payload.date_to = form.date_to;
  }
  return payload;
}

function MenuAvailabilityFields({ form, setForm }: { form: MenuForm; setForm: Dispatch<SetStateAction<MenuForm>> }) {
  const toggleDay = (key: string) => {
    setForm((f) => ({
      ...f,
      days: f.days.includes(key) ? f.days.filter((d) => d !== key) : [...f.days, key],
    }));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label>Name *</Label>
        <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      </div>

      <div className="space-y-2">
        <Label>Schedule</Label>
        <RadioGroup
          value={form.type}
          onValueChange={(v) => setForm((f) => ({ ...f, type: v as MenuForm["type"] }))}
          className="flex gap-4"
        >
          <div className="flex items-center gap-1.5">
            <RadioGroupItem value="daily" id="sched-daily" />
            <Label htmlFor="sched-daily" className="font-normal cursor-pointer">Daily</Label>
          </div>
          <div className="flex items-center gap-1.5">
            <RadioGroupItem value="weekly" id="sched-weekly" />
            <Label htmlFor="sched-weekly" className="font-normal cursor-pointer">Weekly</Label>
          </div>
          <div className="flex items-center gap-1.5">
            <RadioGroupItem value="date_range" id="sched-range" />
            <Label htmlFor="sched-range" className="font-normal cursor-pointer">Date Range</Label>
          </div>
        </RadioGroup>
      </div>

      {(form.type === "daily" || form.type === "weekly") && (
        <div className="space-y-1">
          <Label>Time Window</Label>
          <div className="flex items-center gap-2">
            <input
              type="time"
              value={form.time_start}
              onChange={(e) => setForm((f) => ({ ...f, time_start: e.target.value }))}
              className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <span className="text-sm text-muted-foreground">to</span>
            <input
              type="time"
              value={form.time_end}
              onChange={(e) => setForm((f) => ({ ...f, time_end: e.target.value }))}
              className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
        </div>
      )}

      {form.type === "weekly" && (
        <div className="space-y-1">
          <Label>Days</Label>
          <div className="flex gap-1.5 flex-wrap">
            {SCHEDULE_DAYS.map(({ key, label }, i) => {
              const isChecked = form.days.includes(key);
              return (
                <button
                  key={`${key}-${i}`}
                  type="button"
                  onClick={() => toggleDay(key)}
                  className={`w-8 h-8 rounded-md border text-xs font-medium transition-colors
                    ${isChecked
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-muted-foreground border-border hover-elevate"
                    }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {form.type === "date_range" && (
        <div className="space-y-4">
          <div className="space-y-1">
            <Label>Date Range</Label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={form.date_from}
                onChange={(e) => setForm((f) => ({ ...f, date_from: e.target.value }))}
                className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
              <span className="text-sm text-muted-foreground">to</span>
              <input
                type="date"
                value={form.date_to}
                onChange={(e) => setForm((f) => ({ ...f, date_to: e.target.value }))}
                className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Time Window</Label>
            <div className="flex items-center gap-2">
              <input
                type="time"
                value={form.time_start}
                onChange={(e) => setForm((f) => ({ ...f, time_start: e.target.value }))}
                className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
              <span className="text-sm text-muted-foreground">to</span>
              <input
                type="time"
                value={form.time_end}
                onChange={(e) => setForm((f) => ({ ...f, time_end: e.target.value }))}
                className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MenusManageDialog({ open, onClose, restaurantId, token }: {
  open: boolean; onClose: () => void; restaurantId: string; token: string;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [newMenu, setNewMenu] = useState<MenuForm>({ ...EMPTY_MENU_FORM });
  const [editMenu, setEditMenu] = useState<any>(null);
  const [editForm, setEditForm] = useState<MenuForm>({ ...EMPTY_MENU_FORM });
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);

  const { data: menus = [], isLoading } = useQuery({
    queryKey: ["/api/admin/lam/restaurants", restaurantId, "menus"],
    queryFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/menus`, token),
    enabled: open && !!token,
  });
  const menuList = Array.isArray(menus) ? menus : [];
  const inv = () => qc.invalidateQueries({ queryKey: ["/api/admin/lam/restaurants", restaurantId, "menus"] });

  const addM = useMutation({
    mutationFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/menus`, token, { method: "PUT", body: JSON.stringify(formToPayload(newMenu)) }),
    onSuccess: () => { toast({ title: "Menu added" }); setAddOpen(false); setNewMenu({ ...EMPTY_MENU_FORM }); inv(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });
  const updateM = useMutation({
    mutationFn: ({ id, data }: { id: number; data: MenuForm }) =>
      lamFetch(`/api/admin/lam/restaurants/${restaurantId}/menus/${id}/availability`, token, { method: "POST", body: JSON.stringify(formToPayload(data)) }),
    onSuccess: () => { toast({ title: "Menu updated" }); setEditMenu(null); inv(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });
  const delM = useMutation({
    mutationFn: (id: number) => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/menus/${id}`, token, { method: "DELETE" }),
    onSuccess: () => { toast({ title: "Deleted" }); setDeleteConfirm(null); inv(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex flex-col max-h-[85vh] p-0 max-w-lg">
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <DialogTitle>Manage Menus</DialogTitle>
          <DialogDescription>Configure each menu's name, type, and availability windows.</DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4 space-y-3">
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {menuList.map((m: any) => {
            const id = m.info?.id ?? m.id;
            const info = m.info ?? m;
            const name = info?.name;
            const isExpanded = editMenu?.id === id;
            return (
              <div key={id} className="rounded-md border border-border bg-card overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{name}</p>
                    <div className="flex flex-wrap gap-2 mt-0.5">
                      {info?.type && <span className="text-xs text-muted-foreground">Type: {info.type}</span>}
                      {(info?.availability_type || m?.availability_type) && (
                        <span className="text-xs text-muted-foreground">Avail: {info?.availability_type ?? m?.availability_type}</span>
                      )}
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0"
                    onClick={() => {
                      if (isExpanded) { setEditMenu(null); }
                      else { setEditMenu({ id }); setEditForm(menuToForm(m)); }
                    }}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0"
                    onClick={() => setDeleteConfirm(m)}>
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </div>

                {isExpanded && (
                  <div className="px-3 pb-3 border-t pt-3 space-y-3 bg-muted/30">
                    <MenuAvailabilityFields form={editForm} setForm={setEditForm} />
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditMenu(null)}>Cancel</Button>
                      <Button size="sm" onClick={() => updateM.mutate({ id, data: editForm })} disabled={!editForm.name || updateM.isPending}>
                        {updateM.isPending ? "Saving…" : "Save"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {menuList.length === 0 && !isLoading && <p className="text-sm text-muted-foreground">No menus yet.</p>}
        </div>
        <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => setAddOpen(true)}><Plus className="w-4 h-4 mr-1" /> Add Menu</Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={addOpen} onOpenChange={(v) => !v && setAddOpen(false)}>
        <DialogContent className="flex flex-col max-h-[90vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-0 shrink-0"><DialogTitle>Add Menu</DialogTitle></DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4 space-y-3">
            <MenuAvailabilityFields form={newMenu} setForm={setNewMenu} />
          </div>
          <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t">
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={() => addM.mutate()} disabled={!newMenu.name || addM.isPending}>{addM.isPending ? "Adding…" : "Add Menu"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete menu?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteConfirm?.info?.name ?? deleteConfirm?.name}" will be removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => delM.mutate(deleteConfirm?.info?.id ?? deleteConfirm?.id)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

// ─── MAIN TAB ─────────────────────────────────────────────────────────────────

const EMPTY_CAT_FORM = { name: "", description: "", group: false, controlled: false };
const EMPTY_ITEM_FORM = { name: "", description: "", price: "", category: "", service: ["takeout", "delivery"] as string[], sort: "", csr_note: "" };

export default function MenuEditorTab({ restaurantId, token }: { restaurantId: string; token: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const isMobile = useIsMobile(900);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [selectedMenuId, setSelectedMenuId] = useState<string>("");
  const [selectedCatId, setSelectedCatId] = useState<number | null>(null);
  const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
  const [openDetailsSignal, setOpenDetailsSignal] = useState(0);
  const [showModifierPanel, setShowModifierPanel] = useState(!isMobile);
  const [showMenusDialog, setShowMenusDialog] = useState(false);

  // Auto-collapse on mobile
  useEffect(() => { setShowModifierPanel(!isMobile); }, [isMobile]);

  // Modifier panel ref — used to scroll it into view when an item is expanded
  const modifierPanelRef = useRef<HTMLDivElement>(null);

  // When an item is expanded on desktop, ensure the modifier panel is visible and
  // scroll it into view so drag-and-drop is immediately accessible.
  useEffect(() => {
    if (expandedItemId === null || isMobile) return;
    // Auto-open the modifier panel if it was hidden
    setShowModifierPanel(true);
    // Defer one frame so the panel is rendered before scrollIntoView
    const id = requestAnimationFrame(() => {
      modifierPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(id);
  }, [expandedItemId, isMobile]);

  // Reset item expansion when menu changes to avoid stale itemId queries
  useEffect(() => {
    setExpandedItemId(null);
    setSelectedCatId(null);
  }, [selectedMenuId]);

  // Category dialogs
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [editCat, setEditCat] = useState<any>(null);
  const [deleteCatConfirm, setDeleteCatConfirm] = useState<any>(null);
  const [catForm, setCatForm] = useState({ ...EMPTY_CAT_FORM });
  const [catImageUploading, setCatImageUploading] = useState(false);
  const catImageInputRef = useRef<HTMLInputElement>(null);

  // Item dialogs
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [eightySixTarget, setEightySixTarget] = useState<any>(null);
  const [eightySixCatTarget, setEightySixCatTarget] = useState<any>(null);
  const [eightySixCatPending, setEightySixCatPending] = useState(false);
  const [itemAvailFilter, setItemAvailFilter] = useState<"active" | "86d" | "all">("active");
  const [itemForm, setItemForm] = useState({ ...EMPTY_ITEM_FORM });
  const [uploadingFor, setUploadingFor] = useState<number | null>(null);

  // Inline editing
  const [inlineEditField, setInlineEditField] = useState<string | null>(null);
  const [inlineValue, setInlineValue] = useState("");

  // Local ordering
  const [localCatOrder, setLocalCatOrder] = useState<any[] | null>(null);
  const [localItemOrder, setLocalItemOrder] = useState<any[] | null>(null);

  // Modifier dialogs
  const [editQId, setEditQId] = useState<number | null>(null);
  const [addQOpen, setAddQOpen] = useState(false);
  const [pendingNewQId, setPendingNewQId] = useState<number | null>(null);

  // Link configs keyed `${itemId}-${qId}`
  const [linkConfigs, setLinkConfigs] = useState<Record<string, { required: boolean; order: string }>>({});

  // DnD state
  const [draggingModId, setDraggingModId] = useState<number | null>(null);
  const [overItemDndId, setOverItemDndId] = useState<string | null>(null);

  // Import wizard
  const [importWizardOpen, setImportWizardOpen] = useState(false);

  // Bulk edit state
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkSavingIds, setBulkSavingIds] = useState<Set<number>>(new Set());
  const [bulkFailedIds, setBulkFailedIds] = useState<number[]>([]);
  const [bulkSavedCount, setBulkSavedCount] = useState(0);
  const [adjustPricesOpen, setAdjustPricesOpen] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<number>>(new Set());
  const bulkTableRef = useRef<BulkPriceTableHandle | null>(null);

  // ── Data queries ─────────────────────────────────────────────────────────

  const { data: menus = [] } = useQuery({
    queryKey: ["/api/admin/lam/restaurants", restaurantId, "menus"],
    queryFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/menus`, token),
    enabled: !!token,
  });
  const menuList = Array.isArray(menus) ? menus : [];

  const { data: catsData, isLoading: catsLoading } = useQuery({
    queryKey: ["/api/admin/lam/restaurants", restaurantId, "categories"],
    queryFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/categories`, token),
    enabled: !!token,
  });
  const rawCats = Array.isArray(catsData) ? catsData : (catsData?.categories ?? catsData?.data ?? []);
  const categories: any[] = localCatOrder
    ? localCatOrder
    : [...rawCats].sort((a: any, b: any) => (a.sort ?? a.sort_order ?? 0) - (b.sort ?? b.sort_order ?? 0) || String(a.name).localeCompare(String(b.name)));

  const { data: questionsData, isLoading: questionsLoading } = useQuery({
    queryKey: ["/api/admin/rmp", restaurantId, "questions"],
    queryFn: () => lamFetch(`/api/admin/rmp/${restaurantId}/questions`, token),
    enabled: !!token,
  });
  const allQuestions: any[] = Array.isArray(questionsData) ? questionsData : (questionsData?.questions ?? []);
  const allAnswers: any[] = Array.isArray(questionsData) ? [] : (questionsData?.answers ?? []);

  // Enrich each question with its answers from the flat allAnswers array (when API returns them separately)
  const allQuestionsEnriched: any[] = allQuestions.map((q: any) => {
    if (Array.isArray(q.answers) && q.answers.length > 0) return q;
    if (Array.isArray(q.answer) && q.answer.length > 0) return q;
    const qAnswers = allAnswers.filter((a: any) => a.parent === q.id);
    return qAnswers.length > 0 ? { ...q, answers: qAnswers } : q;
  });

  // After creating a new modifier group, open its edit dialog once it appears in the list
  useEffect(() => {
    if (pendingNewQId === null) return;
    const found = allQuestionsEnriched.find((q: any) => q.id === pendingNewQId);
    if (found) {
      setEditQId(pendingNewQId);
      setPendingNewQId(null);
    }
  }, [pendingNewQId, allQuestionsEnriched]);

  const { data: itemDetail, isLoading: isLoadingItemDetail } = useQuery({
    queryKey: ["/api/admin/rmp", restaurantId, "menus", selectedMenuId, "items", String(expandedItemId)],
    queryFn: () => lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${expandedItemId}`, token),
    enabled: expandedItemId !== null && !!selectedMenuId && !!token,
  });

  // Populate link configs from item detail
  useEffect(() => {
    if (!itemDetail || expandedItemId === null) return;
    const qs: any[] = itemDetail.questions ?? [];
    const newCfgs: Record<string, { required: boolean; order: string }> = {};
    qs.forEach((q: any) => {
      const qId = typeof q === "number" ? q : q?.id;
      if (qId == null) return;
      const key = `${expandedItemId}-${qId}`;
      if (!(key in linkConfigs)) {
        newCfgs[key] = {
          required: q?.required ?? q?.link_required ?? false,
          order: q?.order != null ? String(q.order) : q?.link_order != null ? String(q.link_order) : "",
        };
      }
    });
    if (Object.keys(newCfgs).length > 0) {
      setLinkConfigs((prev) => ({ ...newCfgs, ...prev }));
    }
  }, [itemDetail]);

  // Reset local item order when menu changes
  useEffect(() => { setLocalItemOrder(null); }, [selectedMenuId]);

  // Escape key exits bulk edit mode
  useEffect(() => {
    if (!bulkEditMode) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setBulkEditMode(false);
        setBulkFailedIds([]);
        setBulkSavedCount(0);
        setBulkSelectedIds(new Set());
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [bulkEditMode]);

  const selectedMenu = menuList.find((m: any) => String(m.info?.id ?? m.id) === selectedMenuId);
  const rawMenuItems: any[] = selectedMenu?.items ?? [];
  const allMenuItems: any[] = localItemOrder
    ?? [...rawMenuItems].sort((a: any, b: any) => (a.sort ?? a.sort_order ?? 0) - (b.sort ?? b.sort_order ?? 0));
  const visibleMenuItems: any[] = (() => {
    if (itemAvailFilter === "active") return allMenuItems.filter((it: any) => it.available !== false && it.status !== "deleted" && it.status !== "inactive");
    if (itemAvailFilter === "86d") return allMenuItems.filter((it: any) => it.available === false);
    return allMenuItems;
  })();
  const filteredItems = selectedCatId == null ? visibleMenuItems : visibleMenuItems.filter((it: any) => it.category === selectedCatId);

  function catItemCount(catId: number) { return allMenuItems.filter((it: any) => it.category === catId && it.available !== false && it.status !== "deleted" && it.status !== "inactive").length; }

  // Derive the expanded item from allMenuItems
  const expandedItem: any | null = allMenuItems.find((it: any) => it.id === expandedItemId) ?? null;

  function getLinkedIds(item: any): number[] {
    if (expandedItemId === item.id && itemDetail) {
      return (itemDetail.questions ?? []).map((q: any) => (typeof q === "number" ? q : q?.id)).filter(Boolean);
    }
    // Fall back to React Query cache for items whose detail was previously fetched
    const cachedDetail = qc.getQueryData<any>(["/api/admin/rmp", restaurantId, "menus", selectedMenuId, "items", String(item.id)]);
    if (cachedDetail?.questions) {
      return (cachedDetail.questions as any[]).map((q) => (typeof q === "number" ? q : q?.id)).filter(Boolean);
    }
    if (Array.isArray(item.questions)) return item.questions.map((q: any) => (typeof q === "number" ? q : q?.id)).filter(Boolean);
    return [];
  }

  const expandedLinkedIds: number[] = itemDetail
    ? (itemDetail.questions ?? []).map((q: any) => (typeof q === "number" ? q : q?.id)).filter(Boolean)
    : (expandedItem ? getLinkedIds(expandedItem) : []);

  // Merge list-level expandedItem with detail-level itemDetail so the accordion
  // always shows the most complete data (price, prep_time, sort, etc.)
  const effectiveExpandedItem = useMemo(() => {
    if (expandedItem && itemDetail) {
      // itemDetail.preparation comes from RMP GET: already in minutes.
      // expandedItem.preparation comes from LAM list: in seconds (prep_time * 60).
      // RMP value wins (spread last), so the merged object has minutes already.
      return { ...expandedItem, ...itemDetail };
    }
    if (expandedItem) {
      // Only LAM data available: convert seconds → minutes so the form gets
      // the same unit regardless of whether itemDetail has loaded yet.
      if (expandedItem.preparation != null) {
        return { ...expandedItem, preparation: Math.round(expandedItem.preparation / 60) };
      }
      return expandedItem;
    }
    return null;
  }, [expandedItemId, expandedItem, itemDetail]);

  // ── Invalidation ─────────────────────────────────────────────────────────

  function invMenus() { qc.invalidateQueries({ queryKey: ["/api/admin/lam/restaurants", restaurantId, "menus"] }); }
  function invCats() { qc.invalidateQueries({ queryKey: ["/api/admin/lam/restaurants", restaurantId, "categories"] }); }
  function invQs() { qc.invalidateQueries({ queryKey: ["/api/admin/rmp", restaurantId, "questions"] }); }
  function invItemDetail() {
    if (expandedItemId !== null && selectedMenuId) {
      qc.invalidateQueries({ queryKey: ["/api/admin/rmp", restaurantId, "menus", selectedMenuId, "items", String(expandedItemId)] });
    }
  }

  // ── Category mutations ───────────────────────────────────────────────────

  const addCatM = useMutation({
    mutationFn: () => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/categories`, token, { method: "PUT", body: JSON.stringify({ name: catForm.name, description: catForm.description, group: catForm.group, controlled: catForm.controlled }) }),
    onSuccess: () => { toast({ title: "Category added" }); setAddCatOpen(false); setCatForm({ ...EMPTY_CAT_FORM }); setLocalCatOrder(null); invCats(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });
  const updCatM = useMutation({
    mutationFn: ({ catId, data, silent }: { catId: number; data: any; silent?: boolean }) =>
      lamFetch(`/api/admin/lam/restaurants/${restaurantId}/categories/${catId}`, token, { method: "POST", body: JSON.stringify(data) }),
    onSuccess: (_data, variables) => {
      if (!variables.silent) { toast({ title: "Category updated" }); setEditCat(null); setLocalCatOrder(null); invCats(); }
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });
  const delCatM = useMutation({
    mutationFn: (catId: number) => lamFetch(`/api/admin/lam/restaurants/${restaurantId}/categories/${catId}`, token, { method: "DELETE" }),
    onSuccess: () => { toast({ title: "Deleted" }); setDeleteCatConfirm(null); setLocalCatOrder(null); invCats(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  // ── Item mutations ───────────────────────────────────────────────────────

  const addItemM = useMutation({
    mutationFn: () => lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items`, token, {
      method: "PUT",
      body: JSON.stringify({ name: itemForm.name, description: itemForm.description, price: Number(itemForm.price), sort: itemForm.sort ? Number(itemForm.sort) : 0, category: itemForm.category ? Number(itemForm.category) : undefined, service: itemForm.service, ...(itemForm.csr_note && { csr_note: itemForm.csr_note }) }),
    }),
    onSuccess: () => { toast({ title: "Item added" }); setAddItemOpen(false); setItemForm({ ...EMPTY_ITEM_FORM }); setLocalItemOrder(null); invMenus(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });
  const updItemM = useMutation({
    mutationFn: ({ itemId, data }: { itemId: number; data: any }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${itemId}`, token, { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => { invMenus(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });
  const delItemM = useMutation({
    mutationFn: ({ id }: { id: number; body?: Record<string, any> }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${id}`, token, {
        method: "DELETE",
      }),
    onSuccess: (_data, variables) => {
      toast({ title: "Item 86'd" });
      setEightySixTarget(null);
      setLocalItemOrder(null);
      if (expandedItemId === variables.id) { setExpandedItemId(null); }
      invMenus();
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });
  const restoreItemM = useMutation({
    mutationFn: (id: number) => lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${id}/restore`, token, { method: "PUT" }),
    onSuccess: () => { toast({ title: "Restored" }); invMenus(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });
  const dupItemM = useMutation({
    mutationFn: (id: number) => lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${id}/duplicate`, token, { method: "PUT" }),
    onSuccess: () => { toast({ title: "Duplicated" }); setLocalItemOrder(null); invMenus(); },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  // ── Bulk save helpers ────────────────────────────────────────────────────

  // Returns true on success, false on failure — failure state tracked locally
  async function bulkSaveOneItem(itemId: number, prices: { base?: number | null; takeout?: number | null; delivery?: number | null; catering?: number | null }): Promise<boolean> {
    setBulkSavingIds((prev) => { const s = new Set(prev); s.add(itemId); return s; });
    const body: Record<string, number | null> = {};
    if ("base" in prices) body.price = prices.base ?? null;
    if ("takeout" in prices) body.svc_takeout_price = prices.takeout ?? null;
    if ("delivery" in prices) body.svc_delivery_price = prices.delivery ?? null;
    if ("catering" in prices) body.svc_catering_price = prices.catering ?? null;
    try {
      await lamFetch(
        `/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${itemId}`,
        token,
        { method: "POST", body: JSON.stringify(body) },
      );
      setBulkSavedCount((n) => n + 1);
      setBulkFailedIds((prev) => prev.filter((id) => id !== itemId));
      return true;
    } catch {
      setBulkFailedIds((prev) => prev.includes(itemId) ? prev : [...prev, itemId]);
      return false;
    } finally {
      setBulkSavingIds((prev) => { const s = new Set(prev); s.delete(itemId); return s; });
    }
  }

  async function handleBulkSaveAll(rows: Array<{ itemId: number; prices: { base?: number | null; takeout?: number | null; delivery?: number | null; catering?: number | null } }>) {
    if (rows.length === 0) { toast({ title: "Nothing to save" }); return; }
    setBulkSavedCount(0);
    setBulkFailedIds([]);
    const results = await Promise.all(rows.map((r) => bulkSaveOneItem(r.itemId, r.prices)));
    // Clear dirty for rows that saved successfully
    const succeededIds = rows.filter((_, i) => results[i]).map((r) => r.itemId);
    if (succeededIds.length > 0) bulkTableRef.current?.clearDirty(succeededIds);
    invMenus();
    const failed = results.filter((ok) => !ok).length;
    if (failed === 0) toast({ title: `${rows.length} item${rows.length !== 1 ? "s" : ""} updated` });
    else toast({ title: `${rows.length - failed} saved, ${failed} failed`, variant: "destructive" });
  }

  // ── Link / Unlink ─────────────────────────────────────────────────────────

  const linkM = useMutation({
    mutationFn: ({ itemId, qId, sort }: { itemId: number; qId: number; sort?: number }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${itemId}/questions/link`, token, {
        method: "POST",
        body: JSON.stringify({ id: qId, ...(sort != null && { sort }) }),
      }),
    onSuccess: () => { toast({ title: "Modifier linked" }); invMenus(); invItemDetail(); },
    onError: (e: any) => toast({ title: "Link failed", description: e.message, variant: "destructive" }),
  });
  const unlinkM = useMutation({
    mutationFn: ({ itemId, qId, silent }: { itemId: number; qId: number; silent?: boolean }) =>
      lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${itemId}/questions/unlink/${qId}`, token, { method: "DELETE" }),
    onSuccess: (_data, variables) => {
      if (!variables.silent) toast({ title: "Unlinked" });
      invMenus(); invItemDetail();
    },
    onError: (e: any) => toast({ title: "Unlink failed", description: e.message, variant: "destructive" }),
  });


  // ── Image upload ─────────────────────────────────────────────────────────

  async function handleImageUpload(itemId: number, file: File) {
    setUploadingFor(itemId);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await fetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${itemId}/image`, {
        method: "POST", headers: { "x-eatzy-token": token }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");
      toast({ title: "Image uploaded" });
      invMenus();
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploadingFor(null);
    }
  }

  async function handleCatImageUpload(catId: number, file: File) {
    setCatImageUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await fetch(`/api/admin/lam/restaurants/${restaurantId}/categories/${catId}/image`, {
        method: "POST", headers: { "x-eatzy-token": token }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");
      toast({ title: "Category image uploaded" });
      setEditCat((prev: any) => prev ? { ...prev, image_url: data.image_url } : prev);
      invCats();
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setCatImageUploading(false);
    }
  }

  // ── Inline editing ───────────────────────────────────────────────────────

  function startInline(itemId: number, field: "name" | "price", value: string) {
    setInlineEditField(`${field}-${itemId}`);
    setInlineValue(value);
  }

  function saveInline(itemId: number, field: "name" | "price") {
    setInlineEditField(null);
    const item = allMenuItems.find((it: any) => it.id === itemId);
    if (!item || !inlineValue.trim()) return;
    const base = itemRequiredFields(item);
    if (field === "name" && inlineValue.trim() !== item.name) {
      updItemM.mutate({ itemId, data: { ...base, name: inlineValue.trim() } });
    } else if (field === "price") {
      const num = parseFloat(inlineValue);
      if (!isNaN(num)) updItemM.mutate({ itemId, data: { ...base, price: num } });
    }
  }

  function handleInlineKeyDown(e: ReactKeyboardEvent, itemId: number, field: "name" | "price") {
    if (e.key === "Enter") { e.preventDefault(); saveInline(itemId, field); }
    if (e.key === "Escape") setInlineEditField(null);
  }

  // ── Link config helpers ──────────────────────────────────────────────────

  function getLinkConfig(itemId: number, qId: number) {
    return linkConfigs[`${itemId}-${qId}`] ?? { required: false, order: "" };
  }
  function setLinkConfig(itemId: number, qId: number, cfg: { required: boolean; order: string }) {
    setLinkConfigs((prev) => ({ ...prev, [`${itemId}-${qId}`]: cfg }));
  }

  function doLink(itemId: number, qId: number) {
    const cfg = getLinkConfig(itemId, qId);
    linkM.mutate({ itemId, qId, sort: cfg.order !== "" ? Number(cfg.order) : undefined });
  }
  function doUnlink(itemId: number, qId: number) {
    unlinkM.mutate({ itemId, qId, silent: false });
  }

  function handleLinkConfigChange(itemId: number, linkedIds: number[], qId: number, cfg: { required: boolean; order: string }) {
    setLinkConfig(itemId, qId, cfg);
    if (linkedIds.includes(qId)) {
      const sort = cfg.order !== "" ? Number(cfg.order) : undefined;
      const prevCfg = getLinkConfig(itemId, qId);
      // Silent unlink — user is updating config, not intentionally removing the link
      unlinkM.mutate({ itemId, qId, silent: true }, {
        onSuccess: () => {
          linkM.mutate({ itemId, qId, sort }, {
            onError: () => {
              // Relink failed — restore link with previous config and notify user
              toast({ title: "Update failed", description: "Could not save modifier change. Restoring previous link.", variant: "destructive" });
              setLinkConfig(itemId, qId, prevCfg);
              linkM.mutate({ itemId, qId, sort: prevCfg.order !== "" ? Number(prevCfg.order) : undefined });
            },
          });
        },
        onError: () => {
          setLinkConfig(itemId, qId, prevCfg);
          toast({ title: "Update failed", description: "Could not update modifier link.", variant: "destructive" });
        },
      });
    }
  }

  // ── DnD ──────────────────────────────────────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function onDragStart(event: DragStartEvent) {
    const data = event.active.data.current;
    if (data?.type === "modifier") setDraggingModId(data.questionId);
  }

  function onDragOver(event: DragOverEvent) {
    const { over } = event;
    setOverItemDndId(over ? String(over.id) : null);
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setDraggingModId(null);
    setOverItemDndId(null);
    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // ── Modifier drag-to-link ──
    if (activeData?.type === "modifier") {
      if (overData?.type === "item") {
        const qId: number = activeData.questionId;
        const itemId: number = overData.itemId;
        const item = allMenuItems.find((it: any) => it.id === itemId);
        if (!item) return;
        const linkedIds = getLinkedIds(item);
        if (linkedIds.includes(qId)) {
          toast({ title: "Already linked" });
          return;
        }
        // Compute next available order (max existing link order + 1)
        // Check both local linkConfigs and the item.questions data from the menu response
        let anyOrderFound = false;
        const maxOrder = linkedIds.reduce((max, lqId) => {
          // First try local config (may have been set when sheet was open)
          const cfg = linkConfigs[`${itemId}-${lqId}`];
          if (cfg && cfg.order !== "") { anyOrderFound = true; return Math.max(max, Number(cfg.order)); }
          // Fall back to order from item.questions data
          const qs = Array.isArray(item.questions) ? item.questions : [];
          const linkedQ = qs.find((q: any) => (typeof q === "number" ? q : q?.id) === lqId);
          if (linkedQ && typeof linkedQ === "object" && linkedQ.order != null) { anyOrderFound = true; return Math.max(max, Number(linkedQ.order)); }
          if (linkedQ && typeof linkedQ === "object" && linkedQ.link_order != null) { anyOrderFound = true; return Math.max(max, Number(linkedQ.link_order)); }
          return max;
        }, -1);
        // If no order metadata was found, use the count of existing links as a safe starting point
        const nextOrder = anyOrderFound ? maxOrder + 1 : linkedIds.length;
        // Set config then link
        setLinkConfig(itemId, qId, { required: false, order: String(nextOrder) });
        linkM.mutate({ itemId, qId, sort: nextOrder });
        setExpandedItemId(item.id);
      }
      return;
    }

    if (active.id === over.id) return;

    // ── Category sort ──
    if (activeData?.type === "category" && overData?.type === "category") {
      const ids = categories.map((c: any) => `cat-${c.id}`);
      const oi = ids.indexOf(String(active.id));
      const ni = ids.indexOf(String(over.id));
      if (oi === -1 || ni === -1) return;
      const prevOrder = [...categories];
      const reordered = arrayMove(categories, oi, ni);
      setLocalCatOrder(reordered);
      let failed = false;
      const promises = reordered.map((cat: any, idx: number) =>
        new Promise<void>((resolve) => {
          updCatM.mutate({ catId: cat.id, data: { name: cat.name, sort_order: idx }, silent: true }, {
            onSuccess: () => resolve(),
            onError: () => { failed = true; resolve(); },
          });
        })
      );
      Promise.all(promises).then(() => {
        if (failed) {
          setLocalCatOrder(prevOrder);
          toast({ title: "Reorder failed", description: "Category order was not saved.", variant: "destructive" });
        } else {
          invCats();
        }
      });
      return;
    }

    // ── Item sort (only when a category is selected; reordering across All Items is ambiguous) ──
    if (activeData?.type === "item" && overData?.type === "item") {
      if (selectedCatId === null) return; // Disable reorder in "All Items" view
      const source = filteredItems;
      const ids = source.map((it: any) => `item-${it.id}`);
      const oi = ids.indexOf(String(active.id));
      const ni = ids.indexOf(String(over.id));
      if (oi === -1 || ni === -1) return;
      const reordered = arrayMove(source, oi, ni);
      const merged = [...allMenuItems];
      let ri = 0;
      for (let i = 0; i < merged.length; i++) {
        if (merged[i].category === selectedCatId) merged[i] = reordered[ri++];
      }
      setLocalItemOrder(merged);
      reordered.forEach((it: any, idx: number) => {
        const base = itemRequiredFields(it);
        updItemM.mutate({ itemId: it.id, data: { ...base, sort: idx } });
      });
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <div className="flex flex-col gap-4">
        {/* Top bar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[180px] max-w-xs">
            <Select value={selectedMenuId} onValueChange={setSelectedMenuId}>
              <SelectTrigger><SelectValue placeholder="Select a menu" /></SelectTrigger>
              <SelectContent>
                {menuList.map((m: any) => {
                  const id = String(m.info?.id ?? m.id);
                  return <SelectItem key={id} value={id}>{m.info?.name ?? m.name}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowMenusDialog(true)}>
            <Settings className="w-3 h-3 mr-1" /> Menus
          </Button>
          {!!selectedMenuId && (
            <Button variant="outline" size="sm" onClick={() => setImportWizardOpen(true)}>
              <Wand2 className="w-3 h-3 mr-1" /> Import Menu
            </Button>
          )}
          <div className="ml-auto flex gap-2 flex-wrap">
            {!!selectedMenuId && allMenuItems.length > 0 && (
              <>
                {!bulkEditMode && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => { setBulkEditMode(true); setBulkFailedIds([]); setBulkSavedCount(0); }}
                  >
                    <TableProperties className="w-3 h-3 mr-1" /> Bulk Edit Prices
                  </Button>
                )}
                <Button
                  variant="outline" size="sm"
                  onClick={() => setAdjustPricesOpen(true)}
                >
                  <Percent className="w-3 h-3 mr-1" /> Adjust Prices
                  {bulkSelectedIds.size > 0 && <span className="ml-1 text-xs bg-primary/10 text-primary rounded px-1">{bulkSelectedIds.size}</span>}
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowModifierPanel((v) => !v)}>
              <Columns3 className="w-3 h-3 mr-1" />
              {showModifierPanel ? "Hide" : "Show"} Modifiers
            </Button>
            {!!selectedMenuId && !bulkEditMode && (
              <Button size="sm" onClick={() => {
                setItemForm({ ...EMPTY_ITEM_FORM, category: selectedCatId !== null ? String(selectedCatId) : "" });
                setAddItemOpen(true);
              }}>
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            )}
          </div>
        </div>

        {!selectedMenuId && <p className="text-sm text-muted-foreground">Select a menu above to start editing.</p>}

        {!!selectedMenuId && (
          <div className="flex gap-4 items-start">
            {/* ── LEFT: Categories ────────────────────────────────── */}
            {isMobile ? (
              /* Mobile: category dropdown */
              <div className="w-full mb-1">
                <Select
                  value={selectedCatId === null ? "__all__" : String(selectedCatId)}
                  onValueChange={(v) => setSelectedCatId(v === "__all__" ? null : Number(v))}
                >
                  <SelectTrigger><SelectValue placeholder="All Items" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Items ({allMenuItems.length})</SelectItem>
                    {categories.map((c: any) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.name} ({catItemCount(c.id)})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              /* Desktop: sidebar panel */
              <div className="w-56 shrink-0 flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2 pb-1 border-b">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Categories</span>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { setCatForm({ ...EMPTY_CAT_FORM }); setAddCatOpen(true); }}>
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
                <div
                  onClick={() => setSelectedCatId(null)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover-elevate transition-colors border ${selectedCatId === null ? "bg-primary/10 border-primary/30" : "border-transparent"}`}
                >
                  <UtensilsCrossed className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium flex-1">All Items</span>
                  <Badge variant="secondary" className="text-xs">{allMenuItems.length}</Badge>
                </div>
                <SortableContext items={categories.map((c: any) => `cat-${c.id}`)} strategy={verticalListSortingStrategy}>
                  {categories.map((cat: any) => (
                    <SortableCategoryRow
                      key={cat.id} cat={cat}
                      isSelected={selectedCatId === cat.id}
                      itemCount={catItemCount(cat.id)}
                      onSelect={() => setSelectedCatId(cat.id)}
                      onEdit={() => { setCatForm({ name: cat.name ?? "", description: cat.description ?? "", group: cat.group ?? false, controlled: cat.controlled ?? false }); setEditCat(cat); }}
                      onDelete={() => setDeleteCatConfirm(cat)}
                      onEightySix={() => setEightySixCatTarget(cat)}
                    />
                  ))}
                </SortableContext>
                {catsLoading && <p className="text-xs text-muted-foreground px-2">Loading…</p>}
                {!catsLoading && categories.length === 0 && <p className="text-xs text-muted-foreground px-2">No categories.</p>}
              </div>
            )}

            {/* ── CENTER: Items ────────────────────────────────────── */}
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-1.5 pb-1 border-b flex-wrap gap-y-1">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {bulkEditMode
                    ? `All Items (Bulk Edit) · ${allMenuItems.length}`
                    : selectedCatId != null
                      ? `${categories.find((c: any) => c.id === selectedCatId)?.name ?? "Category"} · ${filteredItems.length}`
                      : `All Items · ${filteredItems.length}`}
                </span>

                {!bulkEditMode && (
                  <>
                    <div className="flex items-center gap-1 ml-auto overflow-x-auto shrink-0">
                      {([
                        { key: "active", label: "Active", count: allMenuItems.filter((it: any) => it.available !== false && it.status !== "deleted").length },
                        { key: "86d", label: "86'd", count: allMenuItems.filter((it: any) => it.available === false).length },
                        { key: "all", label: "All", count: allMenuItems.length },
                      ] as const).map(({ key, label, count }) => (
                        <button key={key} onClick={() => setItemAvailFilter(key)}
                          className={`whitespace-nowrap text-xs px-2 py-0.5 rounded-md border transition-colors flex items-center gap-1 ${
                            itemAvailFilter === key
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-border text-muted-foreground hover-elevate"
                          }`}>
                          {label}
                          <span className={`rounded-full text-[10px] px-1 py-0 leading-4 ${
                            itemAvailFilter === key
                              ? "bg-primary-foreground/20 text-primary-foreground"
                              : "bg-muted text-muted-foreground"
                          }`}>{count}</span>
                        </button>
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground w-full">
                      {draggingModId !== null ? "Drop on an item to link modifier" : "Click name to edit · click price to edit inline"}
                    </span>
                  </>
                )}

                {bulkEditMode && (
                  <>
                    <span className="text-xs text-muted-foreground">Tab to navigate · Enter to save row</span>
                    <div className="flex items-center gap-1.5 ml-auto">
                      {bulkSavingIds.size > 0 && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <RefreshCw className="w-3 h-3 animate-spin" /> Saving…
                        </span>
                      )}
                      {bulkFailedIds.length > 0 && (
                        <span className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {bulkFailedIds.length} failed
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        disabled={bulkSavingIds.size > 0}
                        onClick={() => {
                          const dirty = bulkTableRef.current?.getDirtyRows() ?? [];
                          handleBulkSaveAll(dirty);
                        }}
                      >
                        <Check className="w-3 h-3 mr-1" /> Save All
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => {
                          setBulkEditMode(false);
                          setBulkFailedIds([]);
                          setBulkSavedCount(0);
                          setBulkSelectedIds(new Set());
                        }}
                      >
                        <X className="w-3 h-3 mr-1" /> Exit
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Bulk Edit Table */}
              {bulkEditMode ? (
                <BulkPriceTable
                  ref={bulkTableRef}
                  items={allMenuItems}
                  categories={categories}
                  onSaveRow={(itemId, prices) => bulkSaveOneItem(itemId, prices).then((ok) => { invMenus(); return ok; })}
                  savingIds={bulkSavingIds}
                  failedIds={bulkFailedIds}
                  selectedIds={bulkSelectedIds}
                  onToggleSelected={(id) => setBulkSelectedIds((prev) => {
                    const s = new Set(prev);
                    if (s.has(id)) s.delete(id); else s.add(id);
                    return s;
                  })}
                  showCheckboxes={true}
                />
              ) : selectedCatId === null ? (
                /* ── "All Items" grouped by category ── */
                <>
                  {visibleMenuItems.length === 0 && (
                    <p className="text-sm text-muted-foreground py-4">
                      {itemAvailFilter === "86d" ? "No 86'd items." : "No items yet. Click \"Add Item\" to create one."}
                    </p>
                  )}
                  {[
                    ...categories
                      .map((cat: any) => ({ cat, items: visibleMenuItems.filter((it: any) => it.category === cat.id) }))
                      .filter((g: any) => g.items.length > 0),
                    ...((() => { const u = visibleMenuItems.filter((it: any) => it.category == null); return u.length > 0 ? [{ cat: null, items: u }] : []; })()),
                  ].map(({ cat, items }: { cat: any; items: any[] }) => (
                    <div key={cat?.id ?? "__uncat__"} className="space-y-1.5 mb-4">
                      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm flex items-center justify-between px-1 py-1.5 border-b">
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          {cat?.name ?? "Uncategorized"}
                        </span>
                        <Badge variant="secondary" className="text-xs">{items.length}</Badge>
                      </div>
                      <SortableContext items={items.map((it: any) => `item-${it.id}`)} strategy={verticalListSortingStrategy}>
                        {items.map((item: any) => (
                          <SortableItemRow
                            key={item.id} item={item} categories={categories} hideCategory
                            isExpanded={expandedItemId === item.id}
                            isDropTarget={draggingModId !== null && overItemDndId === `item-${item.id}`}
                            linkedCount={getLinkedIds(item).length}
                            uploadingFor={uploadingFor}
                            editingField={inlineEditField}
                            editingValue={inlineValue}
                            onToggleExpand={() => setExpandedItemId((prev) => prev === item.id ? null : item.id)}
                            onOpenDetails={() => setOpenDetailsSignal((s) => s + 1)}
                            onDelete={() => setEightySixTarget(item)}
                            onRestore={() => restoreItemM.mutate(item.id)}
                            onDuplicate={() => dupItemM.mutate(item.id)}
                            onImageUpload={(file) => handleImageUpload(item.id, file)}
                            onStartInline={(field, value) => startInline(item.id, field, value)}
                            onInlineChange={setInlineValue}
                            onInlineSave={(field) => saveInline(item.id, field)}
                            onInlineKeyDown={(e, field) => handleInlineKeyDown(e, item.id, field)}
                            onInlineCancel={() => setInlineEditField(null)}
                            onEightySix={() => setEightySixTarget(item)}
                            onMakeAvailable={() => restoreItemM.mutate(item.id)}
                            accordionProps={expandedItemId === item.id ? {
                              item: effectiveExpandedItem ?? item,
                              isLoadingDetail: isLoadingItemDetail,
                              categories,
                              restaurantId,
                              menuId: selectedMenuId,
                              token,
                              allQuestions: allQuestionsEnriched,
                              allAnswers,
                              linkedIds: expandedLinkedIds,
                              linkConfigs,
                              onLinkConfigChange: (qId, cfg) => handleLinkConfigChange(item.id, expandedLinkedIds, qId, cfg),
                              onLink: (qId) => doLink(item.id, qId),
                              onUnlink: (qId) => doUnlink(item.id, qId),
                              linkPending: linkM.isPending,
                              unlinkPending: unlinkM.isPending,
                              openDetailsSignal,
                              onImageUpload: (file) => handleImageUpload(item.id, file),
                              uploadingFor,
                            } : undefined}
                          />
                        ))}
                      </SortableContext>
                    </div>
                  ))}
                </>
              ) : (
                /* ── Single category flat list ── */
                <>
                  {filteredItems.length === 0 && (
                    <p className="text-sm text-muted-foreground py-4">
                      {itemAvailFilter === "86d" ? "No 86'd items in this category." : "No items in this category."}
                    </p>
                  )}
                  <SortableContext items={filteredItems.map((it: any) => `item-${it.id}`)} strategy={verticalListSortingStrategy}>
                    {filteredItems.map((item: any) => (
                      <SortableItemRow
                        key={item.id} item={item} categories={categories}
                        isExpanded={expandedItemId === item.id}
                        isDropTarget={draggingModId !== null && overItemDndId === `item-${item.id}`}
                        linkedCount={getLinkedIds(item).length}
                        uploadingFor={uploadingFor}
                        editingField={inlineEditField}
                        editingValue={inlineValue}
                        onToggleExpand={() => setExpandedItemId((prev) => prev === item.id ? null : item.id)}
                        onOpenDetails={() => setOpenDetailsSignal((s) => s + 1)}
                        onDelete={() => setEightySixTarget(item)}
                        onRestore={() => restoreItemM.mutate(item.id)}
                        onDuplicate={() => dupItemM.mutate(item.id)}
                        onImageUpload={(file) => handleImageUpload(item.id, file)}
                        onStartInline={(field, value) => startInline(item.id, field, value)}
                        onInlineChange={setInlineValue}
                        onInlineSave={(field) => saveInline(item.id, field)}
                        onInlineKeyDown={(e, field) => handleInlineKeyDown(e, item.id, field)}
                        onInlineCancel={() => setInlineEditField(null)}
                        onEightySix={() => setEightySixTarget(item)}
                        onMakeAvailable={() => restoreItemM.mutate(item.id)}
                        accordionProps={expandedItemId === item.id ? {
                          item: effectiveExpandedItem ?? item,
                          isLoadingDetail: isLoadingItemDetail,
                          categories,
                          restaurantId,
                          menuId: selectedMenuId,
                          token,
                          allQuestions: allQuestionsEnriched,
                          allAnswers,
                          linkedIds: expandedLinkedIds,
                          linkConfigs,
                          onLinkConfigChange: (qId, cfg) => handleLinkConfigChange(item.id, expandedLinkedIds, qId, cfg),
                          onLink: (qId) => doLink(item.id, qId),
                          onUnlink: (qId) => doUnlink(item.id, qId),
                          linkPending: linkM.isPending,
                          unlinkPending: unlinkM.isPending,
                          openDetailsSignal,
                          onImageUpload: (file) => handleImageUpload(item.id, file),
                          uploadingFor,
                        } : undefined}
                      />
                    ))}
                  </SortableContext>
                </>
              )}
            </div>

            {/* ── RIGHT: Modifier Palette ──────────────────────────── */}
            {showModifierPanel && (
              <div
                ref={modifierPanelRef}
                className={`w-64 shrink-0 flex flex-col gap-2${expandedItemId !== null ? " sticky top-2 self-start" : ""}`}
              >
                <div className="flex items-center justify-between gap-2 pb-1 border-b">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Modifiers</span>
                  <Button size="icon" variant="ghost" title="Create new modifier group" onClick={() => setAddQOpen(true)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {expandedItemId !== null && expandedItem && (
                  <p className="text-xs text-muted-foreground bg-muted/50 rounded-md px-2 py-1.5">
                    Editing: <span className="font-medium">{expandedItem.name}</span>
                    <br />Drag a group onto the item row, or use the Modifiers tab to link with a button.
                  </p>
                )}
                {expandedItemId === null && (
                  <p className="text-xs text-muted-foreground bg-muted/50 rounded-md px-2 py-1.5">
                    Open an item to link modifier groups, or drag a group onto any item row.
                  </p>
                )}

                {questionsLoading && <p className="text-xs text-muted-foreground">Loading…</p>}

                <div className="space-y-2 overflow-y-auto max-h-[calc(100vh-220px)]">
                  {allQuestionsEnriched.map((q: any) => {
                    const isLinked = expandedItemId !== null && expandedItem ? expandedLinkedIds.includes(q.id) : false;
                    const cfg = expandedItemId !== null && expandedItem ? getLinkConfig(expandedItem.id, q.id) : { required: false, order: "" };
                    return (
                      <DraggableModifierRow
                        key={q.id} question={q}
                        isLinked={isLinked} linkConfig={cfg}
                        hasExpandedItem={expandedItemId !== null}
                        linkPending={linkM.isPending} unlinkPending={unlinkM.isPending}
                        onLink={() => { if (expandedItem) doLink(expandedItem.id, q.id); }}
                        onUnlink={() => { if (expandedItem) doUnlink(expandedItem.id, q.id); }}
                        onLinkConfigChange={(newCfg) => {
                          if (!expandedItem) return;
                          handleLinkConfigChange(expandedItem.id, expandedLinkedIds, q.id, newCfg);
                        }}
                        onEdit={() => setEditQId(q.id)}
                      />
                    );
                  })}
                  {!questionsLoading && allQuestionsEnriched.length === 0 && (
                    <p className="text-xs text-muted-foreground">No modifier groups yet. Use the + button above to create one.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* DragOverlay — shows floating card for modifier drag */}
      <DragOverlay>
        {draggingModId !== null && (() => {
          const q = allQuestionsEnriched.find((q: any) => q.id === draggingModId);
          if (!q) return null;
          return (
            <div className="rounded-md border border-primary bg-card px-3 py-2 shadow-md text-sm font-medium opacity-90 flex items-center gap-2">
              <Layers className="w-4 h-4 text-primary shrink-0" />
              {q.name}
            </div>
          );
        })()}
      </DragOverlay>

      {/* ── Dialogs ───────────────────────────────────────────────────── */}
      {/* Add Category */}
      <Dialog open={addCatOpen} onOpenChange={(v) => !v && setAddCatOpen(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Category</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Name *</Label><Input value={catForm.name} onChange={(e) => setCatForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Description</Label><Textarea rows={3} value={catForm.description} onChange={(e) => setCatForm((f) => ({ ...f, description: e.target.value }))} /></div>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  checked={catForm.group}
                  onCheckedChange={(v) => setCatForm((f) => ({ ...f, group: !!v }))}
                />
                <span className="text-sm">Group</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  checked={catForm.controlled}
                  onCheckedChange={(v) => setCatForm((f) => ({ ...f, controlled: !!v }))}
                />
                <span className="text-sm">Controlled</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCatOpen(false)}>Cancel</Button>
            <Button onClick={() => addCatM.mutate()} disabled={!catForm.name || addCatM.isPending}>{addCatM.isPending ? "Adding…" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category */}
      <Dialog open={!!editCat} onOpenChange={(v) => !v && setEditCat(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Category</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Name *</Label><Input value={catForm.name} onChange={(e) => setCatForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Description</Label><Textarea rows={3} value={catForm.description} onChange={(e) => setCatForm((f) => ({ ...f, description: e.target.value }))} /></div>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  checked={catForm.group}
                  onCheckedChange={(v) => setCatForm((f) => ({ ...f, group: !!v }))}
                />
                <span className="text-sm">Group</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  checked={catForm.controlled}
                  onCheckedChange={(v) => setCatForm((f) => ({ ...f, controlled: !!v }))}
                />
                <span className="text-sm">Controlled</span>
              </label>
            </div>
            {/* Photo upload */}
            <div className="space-y-1">
              <Label>Category Photo</Label>
              <div className="flex items-center gap-3">
                {editCat?.image_url && (
                  <img src={editCat.image_url} alt="Category" className="w-14 h-14 rounded-md object-cover shrink-0 border" />
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={catImageUploading}
                  onClick={() => catImageInputRef.current?.click()}
                >
                  {catImageUploading ? <><RefreshCw className="w-3 h-3 mr-1.5 animate-spin" /> Uploading…</> : <><ImageIcon className="w-3 h-3 mr-1.5" /> {editCat?.image_url ? "Change Photo" : "Upload Photo"}</>}
                </Button>
                <input
                  ref={catImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f && editCat?.id) handleCatImageUpload(editCat.id, f);
                    e.target.value = "";
                  }}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCat(null)}>Cancel</Button>
            <Button onClick={() => updCatM.mutate({ catId: editCat.id, data: { name: catForm.name, description: catForm.description, group: catForm.group, controlled: catForm.controlled } })} disabled={!catForm.name || updCatM.isPending}>{updCatM.isPending ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteCatConfirm} onOpenChange={(v) => !v && setDeleteCatConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete category?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteCatConfirm?.name}" will be removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => delCatM.mutate(deleteCatConfirm?.id)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Item */}
      <Dialog open={addItemOpen} onOpenChange={(v) => !v && setAddItemOpen(false)}>
        <DialogContent className="flex flex-col max-h-[90vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-0 shrink-0"><DialogTitle>Add Item</DialogTitle></DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4 space-y-3">
            <div className="space-y-1"><Label>Name *</Label><Input value={itemForm.name} onChange={(e) => setItemForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Description</Label><Textarea value={itemForm.description} onChange={(e) => setItemForm((f) => ({ ...f, description: e.target.value }))} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Base Price</Label><Input type="number" step="0.01" value={itemForm.price} onChange={(e) => setItemForm((f) => ({ ...f, price: e.target.value }))} /></div>
              <div className="space-y-1"><Label>Sort Order</Label><Input type="number" value={itemForm.sort} onChange={(e) => setItemForm((f) => ({ ...f, sort: e.target.value }))} /></div>
            </div>
            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={itemForm.category || "__none__"} onValueChange={(v) => setItemForm((f) => ({ ...f, category: v === "__none__" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {categories.map((c: any) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Services</Label>
              <div className="flex flex-wrap gap-3">
                {["takeout", "delivery", "catering"].map((svc) => (
                  <label key={svc} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="checkbox" checked={itemForm.service.includes(svc)}
                      onChange={() => setItemForm((f) => ({ ...f, service: f.service.includes(svc) ? f.service.filter((s) => s !== svc) : [...f.service, svc] }))} />
                    {svc}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 pb-6 pt-4 shrink-0 border-t">
            <Button variant="outline" onClick={() => setAddItemOpen(false)}>Cancel</Button>
            <Button onClick={() => addItemM.mutate()} disabled={!itemForm.name || addItemM.isPending}>{addItemM.isPending ? "Adding…" : "Add Item"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EightySixModal
        open={!!eightySixTarget}
        item={eightySixTarget}
        onClose={() => setEightySixTarget(null)}
        onConfirm={(body, useUpdate) => {
          if (!eightySixTarget) return;
          if (useUpdate) {
            const base = itemRequiredFields(eightySixTarget);
            updItemM.mutate({ itemId: eightySixTarget.id, data: { ...base, ...body } }, {
              onSuccess: () => {
                toast({ title: "Item 86'd" });
                setEightySixTarget(null);
              },
            });
          } else {
            delItemM.mutate({ id: eightySixTarget.id });
          }
        }}
        isPending={delItemM.isPending || updItemM.isPending}
      />

      {/* 86 all items in a category */}
      <EightySixModal
        open={!!eightySixCatTarget}
        item={eightySixCatTarget ? { name: `all ${catItemCount(eightySixCatTarget.id)} item${catItemCount(eightySixCatTarget.id) !== 1 ? "s" : ""} in ${eightySixCatTarget.name}` } : null}
        onClose={() => setEightySixCatTarget(null)}
        isPending={eightySixCatPending}
        onConfirm={async (body, useUpdate) => {
          if (!eightySixCatTarget) return;
          const activeItems = allMenuItems.filter((it: any) =>
            it.category === eightySixCatTarget.id &&
            it.available !== false &&
            it.status !== "deleted" &&
            it.status !== "inactive"
          );
          if (activeItems.length === 0) { setEightySixCatTarget(null); return; }
          setEightySixCatPending(true);
          const results = await Promise.allSettled(
            activeItems.map((it: any) => {
              if (useUpdate) {
                const base = itemRequiredFields(it);
                return lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${it.id}`, token, {
                  method: "POST", body: JSON.stringify({ ...base, ...body }),
                });
              } else {
                return lamFetch(`/api/admin/rmp/${restaurantId}/menus/${selectedMenuId}/items/${it.id}`, token, {
                  method: "DELETE",
                });
              }
            })
          );
          setEightySixCatPending(false);
          const failed = results.filter((r) => r.status === "rejected").length;
          const succeeded = results.length - failed;
          if (failed === 0) {
            toast({ title: `${succeeded} item${succeeded !== 1 ? "s" : ""} 86'd` });
          } else if (succeeded === 0) {
            toast({ title: `Failed to 86 items (${failed} error${failed !== 1 ? "s" : ""})`, variant: "destructive" });
          } else {
            toast({ title: `${succeeded} 86'd, ${failed} failed`, variant: "destructive" });
          }
          setLocalItemOrder(null);
          setEightySixCatTarget(null);
          invMenus();
        }}
      />

      {/* Create Modifier Group */}
      <EditModifierGroupDialog
        open={addQOpen}
        onClose={() => setAddQOpen(false)}
        question={null}
        restaurantId={restaurantId}
        token={token}
        onRefresh={invQs}
        onCreated={(newId) => setPendingNewQId(newId)}
      />

      {/* Edit Modifier Group */}
      <EditModifierGroupDialog
        open={editQId !== null}
        onClose={() => setEditQId(null)}
        question={editQId !== null ? allQuestionsEnriched.find((q: any) => q.id === editQId) ?? null : null}
        restaurantId={restaurantId}
        token={token}
        onRefresh={invQs}
      />

      <MenusManageDialog open={showMenusDialog} onClose={() => setShowMenusDialog(false)} restaurantId={restaurantId} token={token} />

      <MenuImportWizard
        open={importWizardOpen}
        onClose={() => { setImportWizardOpen(false); invMenus(); }}
        restaurantId={restaurantId}
        menuId={selectedMenuId}
        token={token}
      />

      {adjustPricesOpen && (
        <AdjustPricesDialog
          open={adjustPricesOpen}
          onClose={() => setAdjustPricesOpen(false)}
          items={allMenuItems}
          categories={categories}
          selectedCatId={selectedCatId}
          selectedIds={bulkSelectedIds}
          onSaveOne={(itemId, prices) => bulkSaveOneItem(itemId, prices)}
          onDone={() => invMenus()}
          restaurantId={restaurantId}
          token={token}
          hasBulkSelection={bulkEditMode && bulkSelectedIds.size > 0}
          onEnterBulkEdit={() => setBulkEditMode(true)}
        />
      )}
    </DndContext>
  );
}
